// SAAS Super Rate System module internationalization - English
export default {
  title: 'SAAS Super Rate System',
  description: 'Intelligent rate query and management system',

  // Third Party Address Select Component
  thirdPartyAddressSelect: {
    country: 'Country',
    state: 'State',
    city: 'City',
    district: 'District',
    postalCode: 'Postal Code (Optional)',
    postalCodePlaceholder: 'Enter postal code',
    detailAddress: 'Detail Address',
    detailAddressPlaceholder: 'Enter detail address',
    countryPlaceholder: 'Select country',
    statePlaceholder: 'Select state',
    cityPlaceholder: 'Select city',
    districtPlaceholder: 'Select district'
  },

  // Rate Query
  rateQuery: {
    title: 'Rate Query',
    description: 'Query FCL, LCL, air freight, precarriage and last mile rates',
    filterTitle: 'Filter Conditions',
    totalCount: 'Total {count} records',

    // Tabs
    tabs: {
      fcl: 'FCL Rates',
      lcl: 'LCL Rates',
      air: 'Air Freight Rates',
      precarriage: 'Precarriage Rates',
      oncarriage: 'Last Mile Rates'
    },

    // Filters
    filters: {
      carrier: 'Carrier',
      carrierPlaceholder: 'Select carrier',
      transitType: 'Direct/Transit',
      transitTypePlaceholder: 'Select direct/transit',
      validPeriod: 'Valid Period',
      etdRange: 'ETD Range',
      routeName: 'Route Name',
      routeNamePlaceholder: 'Select route name',
      routeCode: 'Route Code',
      routeCodePlaceholder: 'Select route code',
      departurePort: 'Departure Port',
      departurePortPlaceholder: 'Select departure port',
      dischargePort: 'Discharge Port',
      dischargePortPlaceholder: 'Select discharge port',
      // Extended filter conditions
      priceNo: 'Rate No.',
      priceNoPlaceholder: 'Enter rate number',
      rateType: 'Rate Type',
      rateTypePlaceholder: 'Select rate type',
      status: 'Status',
      statusPlaceholder: 'Select status',
      cargoType: 'Cargo Type',
      cargoTypePlaceholder: 'Select cargo type',
      contractNo: 'Contract No.',
      contractNoPlaceholder: 'Enter contract number',
      vesselName: 'Vessel Name',
      vesselNamePlaceholder: 'Enter vessel name',
      voyageNo: 'Voyage No.',
      voyageNoPlaceholder: 'Enter voyage number',
      weekdays: 'Vessel Schedule',
      weekdaysPlaceholder: 'Select vessel schedule',
      // Time filters
      beginDate: 'Effective Date',
      beginDatePlaceholder: 'Select effective date',
      endDate: 'Expiry Date',
      endDatePlaceholder: 'Select expiry date',
      createTime: 'Create Time',
      createTimePlaceholder: 'Select create time range',
      updateTime: 'Update Time',
      updateTimePlaceholder: 'Select update time range'
    },

    // Options
    options: {
      direct: 'Direct',
      transit: 'Transit',
      // Status options
      enabled: 'Enabled',
      disabled: 'Disabled',
      draft: 'Draft',
      normal: 'Normal',
      expired: 'Expired',
      offline: 'Offline',
      // Rate type options
      contract: 'Contract',
      spot: 'SPOT E-commerce',
      // Cargo type options
      general: 'General',
      dangerous: 'Dangerous',
      frozen: 'Frozen',
      special: 'Special',
      steelCoil: 'Steel Coil',
      liquid: 'Liquid',
      chemical: 'Chemical',
      textile: 'Textile',
      // Space status options
      smooth: 'Smooth',
      singleApply: 'Single Apply',
      overbooked: 'Overbooked',
      notAccepting: 'Not Accepting',
      // Price trend options
      rising: 'Rising',
      falling: 'Falling',
      stable: 'Stable',
      // Rate query specific options
      queryStatus: {
        pending: 'Querying',
        completed: 'Query Completed',
        failed: 'Query Failed',
        cancelled: 'Query Cancelled'
      },
      // Combination query solution status
      solutionStatus: {
        available: 'Available',
        unavailable: 'Unavailable',
        pending: 'Pending',
        confirmed: 'Confirmed',
        rejected: 'Rejected'
      },
      // Match level
      matchLevel: {
        excellent: 'Excellent',
        good: 'Good',
        fair: 'Fair',
        poor: 'Poor'
      },
      // Risk level
      riskLevel: {
        low: 'Low Risk',
        medium: 'Medium Risk',
        high: 'High Risk'
      },
      // Feasibility level
      feasibility: {
        highlyFeasible: 'Highly Feasible',
        feasible: 'Feasible',
        partiallyFeasible: 'Partially Feasible',
        notFeasible: 'Not Feasible'
      }
    },

    // Routes
    routes: {
      asiaIntra: 'Intra Asia Route',
      feSea: 'Far East - Southeast Asia Route',
      feMe: 'Far East - Middle East Route',
      // Major routes
      transPacific: 'Trans-Pacific Route',
      transAtlantic: 'Trans-Atlantic Route',
      europeAsia: 'Europe-Asia Route',
      mediterranean: 'Mediterranean Route',
      redSea: 'Red Sea Route',
      indianOcean: 'Indian Ocean Route',
      // Regional routes
      northAsia: 'North Asia Route',
      southeastAsia: 'Southeast Asia Route',
      southAsia: 'South Asia Route',
      westAsia: 'West Asia Route',
      africa: 'Africa Route',
      australia: 'Australia Route',
      // Special routes
      arctic: 'Arctic Route',
      suez: 'Suez Canal Route',
      panama: 'Panama Canal Route'
    },

    // Ports
    ports: {
      shanghai: 'Shanghai',
      ningbo: 'Ningbo',
      qingdao: 'Qingdao',
      // Major Chinese ports
      xiamen: 'Xiamen',
      guangzhou: 'Guangzhou',
      shenzhen: 'Shenzhen',
      tianjin: 'Tianjin',
      dalian: 'Dalian',
      yantai: 'Yantai',
      lianyungang: 'Lianyungang',
      // Major Asian ports
      busan: 'Busan',
      tokyo: 'Tokyo',
      yokohama: 'Yokohama',
      singapore: 'Singapore',
      portKlang: 'Port Klang',
      laemChabang: 'Laem Chabang',
      // Major European ports
      rotterdam: 'Rotterdam',
      hamburg: 'Hamburg',
      antwerp: 'Antwerp',
      leHavre: 'Le Havre',
      // Major American ports
      losAngeles: 'Los Angeles',
      longBeach: 'Long Beach',
      newYork: 'New York',
      savannah: 'Savannah'
    },

    // Carriers
    carriers: {
      msc: 'Mediterranean Shipping Company',
      maersk: 'Maersk Line',
      cmaCgm: 'CMA CGM',
      cosco: 'COSCO Shipping',
      evergreen: 'Evergreen Marine',
      yangMing: 'Yang Ming Marine',
      hmm: 'Hyundai Merchant Marine',
      one: 'Ocean Network Express',
      zim: 'ZIM Integrated Shipping',
      hpl: 'Hapag-Lloyd',
      oocl: 'Orient Overseas Container Line',
      csc: 'COSCO Container Lines'
    },

    // Currencies
    currencies: {
      usd: 'US Dollar',
      cny: 'Chinese Yuan',
      eur: 'Euro',
      jpy: 'Japanese Yen',
      gbp: 'British Pound',
      hkd: 'Hong Kong Dollar',
      sgd: 'Singapore Dollar',
      krw: 'Korean Won',
      thb: 'Thai Baht',
      myr: 'Malaysian Ringgit',
      idr: 'Indonesian Rupiah',
      php: 'Philippine Peso'
    },

    // Billing Units
    billingUnits: {
      teu: 'TEU',
      cbm: 'CBM',
      kgs: 'KGS',
      pcs: 'PCS',
      set: 'SET',
      pallet: 'Pallet',
      container: 'Container',
      ton: 'Ton',
      meter: 'Meter',
      square: 'Square Meter'
    },

    // Charge Items
    chargeItems: {
      // Ocean freight charges
      oceanFreight: 'Ocean Freight',
      thc: 'Terminal Handling Charge',
      doc: 'Documentation Fee',
      seal: 'Seal Fee',
      // Additional charges
      baf: 'Bunker Adjustment Factor',
      caf: 'Currency Adjustment Factor',
      ebs: 'Emergency Bunker Surcharge',
      ccs: 'Container Imbalance Charge',
      // Port charges
      portCharges: 'Port Charges',
      terminalHandling: 'Terminal Handling',
      wharfage: 'Wharfage',
      // Other charges
      customsClearance: 'Customs Clearance',
      inspection: 'Inspection Fee',
      insurance: 'Insurance Fee',
      storage: 'Storage Fee'
    },

    // Container Types
    containerTypes: {
      // Standard containers
      gp20: '20GP',
      gp40: '40GP',
      hc40: '40HC',
      hc45: '45HC',
      // Special containers
      rf20: '20RF',
      rf40: '40RF',
      tk20: '20TK',
      tk40: '40TK',
      ot20: '20OT',
      ot40: '40OT',
      fr20: '20FR',
      fr40: '40FR',
      // Other container types
      nor20: '20NOR',
      nor40: '40NOR',
      flat20: '20FLAT',
      flat40: '40FLAT'
    },

    // Weekdays
    weekdays: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
      // Abbreviations
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun'
    },

    // Pagination
    pagination: {
      total: 'Total {total} items',
      pageSize: '{pageSize} items per page',
      current: 'Page {current}',
      goto: 'Go to',
      page: 'page',
      of: 'of',
      items: 'items',
      showTotal: 'Showing {start} to {end} of {total} items',
      showSizeChanger: 'Show size',
      showQuickJumper: 'Quick jump',
      showLessItems: 'Show less pages',
      showMoreItems: 'Show more pages'
    },

    // Export
    export: {
      title: 'Export Data',
      filename: 'Rate Query Results',
      format: 'Export Format',
      excel: 'Excel File',
      csv: 'CSV File',
      pdf: 'PDF File',
      allColumns: 'All Columns',
      selectedColumns: 'Selected Columns',
      customColumns: 'Custom Columns',
      download: 'Download',
      cancel: 'Cancel',
      processing: 'Exporting...',
      success: 'Export successful',
      failed: 'Export failed'
    },

    // Table operations
    table: {
      // Toolbar
      toolbar: {
        refresh: 'Refresh',
        density: 'Density',
        columnSetting: 'Column Settings',
        fullScreen: 'Full Screen',
        exitFullScreen: 'Exit Full Screen',
        reload: 'Reload',
        export: 'Export',
        import: 'Import',
        print: 'Print'
      },
      // Row actions
      rowActions: {
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        copy: 'Copy',
        enable: 'Enable',
        disable: 'Disable',
        more: 'More'
      },
      // Batch actions
      batchActions: {
        selectAll: 'Select All',
        unselectAll: 'Unselect All',
        selectedCount: 'Selected {count} items',
        batchDelete: 'Batch Delete',
        batchEnable: 'Batch Enable',
        batchDisable: 'Batch Disable',
        batchExport: 'Batch Export'
      }
    },

    // Search and filter
    search: {
      // Search box
      searchBox: {
        placeholder: 'Enter keywords to search',
        searchButton: 'Search',
        clearButton: 'Clear',
        advancedSearch: 'Advanced Search',
        simpleSearch: 'Simple Search'
      },
      // Advanced filter
      advancedFilter: {
        title: 'Advanced Filter',
        expand: 'Expand',
        collapse: 'Collapse',
        addCondition: 'Add Condition',
        removeCondition: 'Remove Condition',
        clearAll: 'Clear All',
        apply: 'Apply',
        reset: 'Reset',
        saveAsDefault: 'Save as Default',
        loadDefault: 'Load Default'
      },
      // Filter conditions
      filterConditions: {
        and: 'AND',
        or: 'OR',
        equals: 'Equals',
        notEquals: 'Not Equals',
        contains: 'Contains',
        notContains: 'Not Contains',
        startsWith: 'Starts With',
        endsWith: 'Ends With',
        greaterThan: 'Greater Than',
        greaterThanOrEqual: 'Greater Than or Equal',
        lessThan: 'Less Than',
        lessThanOrEqual: 'Less Than or Equal',
        between: 'Between',
        in: 'In',
        notIn: 'Not In',
        isNull: 'Is Null',
        isNotNull: 'Is Not Null'
      }
    },

    // Status and tips
    status: {
      // Loading status
      loading: 'Loading...',
      loaded: 'Loaded',
      loadingFailed: 'Loading failed',
      // Operation status
      processing: 'Processing...',
      success: 'Operation successful',
      failed: 'Operation failed',
      cancelled: 'Operation cancelled',
      // Data status
      noData: 'No data',
      emptyData: 'Data is empty',
      partialData: 'Partial data loading failed',
      // Network status
      networkError: 'Network error',
      serverError: 'Server error',
      timeout: 'Request timeout',
      unauthorized: 'Unauthorized access',
      forbidden: 'Access forbidden',
      notFound: 'Resource not found'
    },

    // Tips
    tips: {
      // Operation tips
      clickToView: 'Click to view details',
      clickToEdit: 'Click to edit',
      clickToDelete: 'Click to delete',
      clickToEnable: 'Click to enable',
      clickToDisable: 'Click to disable',
      // Selection tips
      selectItems: 'Please select items to operate',
      selectAtLeastOne: 'Please select at least one item',
      selectMaxItems: 'Maximum {max} items can be selected',
      // Confirmation tips
      confirmOperation: 'Confirm to execute this operation?',
      operationIrreversible: 'This operation is irreversible',
      dataWillBeLost: 'Data will be lost',
      // Help tips
      help: 'Help',
      moreInfo: 'More Information',
      contactSupport: 'Contact Support'
    },

    // Date and time
    dateTime: {
      // Date formats
      formats: {
        date: 'YYYY-MM-DD',
        dateTime: 'YYYY-MM-DD HH:mm:ss',
        time: 'HH:mm:ss',
        month: 'YYYY-MM',
        year: 'YYYY'
      },
      // Date labels
      labels: {
        today: 'Today',
        yesterday: 'Yesterday',
        tomorrow: 'Tomorrow',
        thisWeek: 'This Week',
        lastWeek: 'Last Week',
        nextWeek: 'Next Week',
        thisMonth: 'This Month',
        lastMonth: 'Last Month',
        nextMonth: 'Next Month',
        thisYear: 'This Year',
        lastYear: 'Last Year',
        nextYear: 'Next Year'
      },
      // Time labels
      timeLabels: {
        now: 'Now',
        morning: 'Morning',
        afternoon: 'Afternoon',
        evening: 'Evening',
        night: 'Night'
      },
      // Relative time
      relative: {
        justNow: 'Just now',
        minutesAgo: '{minutes} minutes ago',
        hoursAgo: '{hours} hours ago',
        daysAgo: '{days} days ago',
        weeksAgo: '{weeks} weeks ago',
        monthsAgo: '{months} months ago',
        yearsAgo: '{years} years ago'
      }
    },

    // Errors and exceptions
    errors: {
      // General errors
      general: {
        unknown: 'Unknown error',
        systemError: 'System error',
        networkError: 'Network error',
        serverError: 'Server error',
        clientError: 'Client error',
        timeout: 'Request timeout',
        connectionFailed: 'Connection failed'
      },
      // Data errors
      data: {
        invalidData: 'Invalid data',
        dataNotFound: 'Data not found',
        dataCorrupted: 'Data corrupted',
        dataIncomplete: 'Data incomplete',
        dataMismatch: 'Data mismatch',
        dataExpired: 'Data expired'
      },
      // Permission errors
      permission: {
        unauthorized: 'Unauthorized',
        forbidden: 'Access forbidden',
        insufficientPrivileges: 'Insufficient privileges',
        accessDenied: 'Access denied',
        loginRequired: 'Login required',
        sessionExpired: 'Session expired'
      },
      // Validation errors
      validation: {
        required: 'This field is required',
        invalidFormat: 'Invalid format',
        tooLong: 'Content too long',
        tooShort: 'Content too short',
        invalidValue: 'Invalid value',
        duplicateValue: 'Duplicate value'
      },
      // Message tips
      messages: {
        exportTaskFailed: 'Failed to trigger export task, please try again later',
        selectRecordsFirst: 'Please select records to export first',
        noDataToExport: 'No data to export',
        exportFailed: '{type} export failed, please try again later'
      }
    },

    // Success and confirmation
    success: {
      // Operation success
      operations: {
        create: 'Created successfully',
        update: 'Updated successfully',
        delete: 'Deleted successfully',
        save: 'Saved successfully',
        submit: 'Submitted successfully',
        approve: 'Approved successfully',
        reject: 'Rejected successfully',
        enable: 'Enabled successfully',
        disable: 'Disabled successfully',
        import: 'Imported successfully',
        export: 'Exported successfully'
      },
      // Status changes
      status: {
        activated: 'Activated',
        deactivated: 'Deactivated',
        published: 'Published',
        unpublished: 'Unpublished',
        archived: 'Archived',
        restored: 'Restored',
        locked: 'Locked',
        unlocked: 'Unlocked'
      },
      // Message tips
      messages: {
        operationCompleted: 'Operation completed',
        dataSaved: 'Data saved',
        changesApplied: 'Changes applied',
        settingsUpdated: 'Settings updated',
        configurationSaved: 'Configuration saved',
        exportTaskTriggered: 'Export task triggered successfully, please check the export results later',
        exportSuccess: '{type} export successful, exported {count} records'
      }
    },

    // Confirmation dialogs
    confirm: {
      // General confirmation
      general: {
        title: 'Confirm Operation',
        message: 'Are you sure you want to execute this operation?',
        okText: 'OK',
        cancelText: 'Cancel',
        yesText: 'Yes',
        noText: 'No'
      },
      // Delete confirmation
      delete: {
        title: 'Confirm Delete',
        message: 'Are you sure you want to delete this record?',
        batchMessage: 'Are you sure you want to delete the selected {count} records?',
        warning: 'Cannot be recovered after deletion',
        permanent: 'This operation is irreversible'
      },
      // Status change confirmation
      status: {
        enable: {
          title: 'Confirm Enable',
          message: 'Are you sure you want to enable this record?',
          batchMessage: 'Are you sure you want to enable the selected {count} records?'
        },
        disable: {
          title: 'Confirm Disable',
          message: 'Are you sure you want to disable this record?',
          batchMessage: 'Are you sure you want to disable the selected {count} records?'
        }
      },
      // Save confirmation
      save: {
        title: 'Confirm Save',
        message: 'Are you sure you want to save the changes?',
        unsavedChanges: 'You have unsaved changes, are you sure you want to leave?',
        discardChanges: 'Are you sure you want to discard the changes?'
      }
    },

    // Help and documentation
    help: {
      // Help menu
      menu: {
        help: 'Help',
        userGuide: 'User Guide',
        faq: 'FAQ',
        contactSupport: 'Contact Support',
        about: 'About System',
        feedback: 'Feedback'
      },
      // User guide
      userGuide: {
        title: 'User Guide',
        gettingStarted: 'Getting Started',
        basicOperations: 'Basic Operations',
        advancedFeatures: 'Advanced Features',
        troubleshooting: 'Troubleshooting',
        shortcuts: 'Shortcuts'
      },
      // FAQ
      faq: {
        title: 'FAQ',
        search: 'Search Questions',
        categories: 'Categories',
        popular: 'Popular Questions',
        recent: 'Recent Questions'
      },
      // Contact support
      support: {
        title: 'Contact Support',
        email: 'Email Support',
        phone: 'Phone Support',
        onlineChat: 'Online Chat',
        ticket: 'Ticket System',
        responseTime: 'Response Time'
      }
    },

    // System settings
    settings: {
      // Basic settings
      basic: {
        title: 'Basic Settings',
        language: 'Language Settings',
        timezone: 'Timezone Settings',
        dateFormat: 'Date Format',
        timeFormat: 'Time Format',
        currency: 'Currency Settings',
        units: 'Unit Settings'
      },
      // Display settings
      display: {
        title: 'Display Settings',
        theme: 'Theme Settings',
        fontSize: 'Font Size',
        colorScheme: 'Color Scheme',
        layout: 'Layout Settings',
        density: 'Density Settings'
      },
      // Notification settings
      notification: {
        title: 'Notification Settings',
        email: 'Email Notifications',
        sms: 'SMS Notifications',
        push: 'Push Notifications',
        sound: 'Sound Alerts',
        frequency: 'Notification Frequency'
      },
      // Security settings
      security: {
        title: 'Security Settings',
        password: 'Password Settings',
        twoFactor: 'Two-Factor Authentication',
        session: 'Session Management',
        permissions: 'Permission Management',
        audit: 'Audit Logs'
      }
    },

    // User and permissions
    user: {
      // User management
      management: {
        title: 'User Management',
        userList: 'User List',
        addUser: 'Add User',
        editUser: 'Edit User',
        deleteUser: 'Delete User',
        userProfile: 'User Profile',
        changePassword: 'Change Password'
      },
      // Role management
      roles: {
        title: 'Role Management',
        roleList: 'Role List',
        addRole: 'Add Role',
        editRole: 'Edit Role',
        deleteRole: 'Delete Role',
        assignRole: 'Assign Role',
        permissions: 'Permission Settings'
      },
      // Permission management
      permissions: {
        title: 'Permission Management',
        permissionList: 'Permission List',
        addPermission: 'Add Permission',
        editPermission: 'Edit Permission',
        deletePermission: 'Delete Permission',
        assignPermission: 'Assign Permission',
        permissionGroups: 'Permission Groups'
      },
      // User status
      status: {
        active: 'Active',
        inactive: 'Inactive',
        locked: 'Locked',
        suspended: 'Suspended',
        pending: 'Pending',
        expired: 'Expired'
      }
    },

    // Reports and statistics
    reports: {
      // Report management
      management: {
        title: 'Report Management',
        reportList: 'Report List',
        createReport: 'Create Report',
        editReport: 'Edit Report',
        deleteReport: 'Delete Report',
        scheduleReport: 'Schedule Report',
        reportTemplates: 'Report Templates'
      },
      // Statistical charts
      charts: {
        title: 'Statistical Charts',
        lineChart: 'Line Chart',
        barChart: 'Bar Chart',
        pieChart: 'Pie Chart',
        areaChart: 'Area Chart',
        scatterChart: 'Scatter Chart',
        radarChart: 'Radar Chart'
      },
      // Data dimensions
      dimensions: {
        title: 'Data Dimensions',
        time: 'Time Dimension',
        region: 'Region Dimension',
        product: 'Product Dimension',
        customer: 'Customer Dimension',
        channel: 'Channel Dimension',
        category: 'Category Dimension'
      },
      // Report types
      types: {
        title: 'Report Types',
        summary: 'Summary Report',
        detail: 'Detail Report',
        comparison: 'Comparison Report',
        trend: 'Trend Report',
        distribution: 'Distribution Report',
        custom: 'Custom Report'
      }
    },

    // Table Columns
    columns: {
      carrier: 'Carrier',
      dischargePort: 'Discharge Port',
      transitType: 'Direct/Transit',
      transit: 'Transit Days',
      routeCode: 'Route Code',
      vesselName: 'Vessel Name',
      voyageNo: 'Voyage No',
      spaceStatus: 'Space Status',
      remark: 'Remark',
      weight: 'Weight(KGS)',
      volume: 'Volume(CBM)',
      // Common columns
      actions: 'Actions',
      status: 'Status',
      createTime: 'Create Time',
      updateTime: 'Update Time',
      createBy: 'Created By',
      updateBy: 'Updated By',
      // Rate related columns
      priceNo: 'Rate No.',
      rateType: 'Rate Type',
      pol: 'Port of Loading',
      pod: 'Port of Discharge',
      pot: 'Port of Transit',
      contractNo: 'Contract No.',
      validPeriod: 'Valid Period',
      etd: 'ETD',
      eta: 'ETA',
      // Fee related columns
      freight: 'Freight',
      currency: 'Currency',
      chargeItem: 'Charge Item',
      billingUnit: 'Billing Unit',
      // Rate query specific columns
      queryTime: 'Query Time',
      queryUser: 'Query User',
      queryResult: 'Query Result',
      matchScore: 'Match Score',
      totalPrice: 'Total Price',
      priceBreakdown: 'Price Breakdown',
      // Combination query related columns
      solutionType: 'Solution Type',
      solutionName: 'Solution Name',
      solutionDescription: 'Solution Description',
      feasibility: 'Feasibility',
      estimatedTime: 'Estimated Time',
      riskLevel: 'Risk Level'
    },

    // FCL Filter Fields Internationalization
    filterFields: {
      // Labels
      labels: {
        routeCode: 'Rate Code',
        rateType: 'Rate Type',
        departurePort: 'Departure Port',
        dischargePort: 'Discharge Port',
        directTransit: 'Direct/Transit',
        transitPort: 'Transit Port',
        transitType: 'Transit Type',
        shipCompany: 'Shipping Company',
        contractNo: 'Contract No',
        spaceStatus: 'Space Status',
        priceStatus: 'Price Trend',
        cargoType: 'Cargo Type',
        vesselSchedule: 'Vessel Schedule',
        voyage: 'Voyage',
        freeStorageDays: 'Free Storage Days',
        nac: 'NAC',
        validPeriod: 'Valid Period',
        etd: 'ETD',
        eta: 'ETA',
        vesselName: 'Vessel Name',
        voyageNo: 'Voyage No',
        cutoffDate: 'Cutoff Date',
        destinationRegion: 'Destination Region',
        entryPerson: 'Created By',
        createDate: 'Create Date',
        rateModifier: 'Rate Modifier',
        modifyDate: 'Modify Date',
        rateStatus: 'Rate Status',
        routeName: 'Route Name',
        routeCodeSelect: 'Route Code',
        terminal: 'Terminal',
        branch: 'Branch'
      },

      // Placeholders
      placeholders: {
        routeCode: 'Enter rate code',
        rateType: 'Select rate type',
        departurePort: 'Select departure port',
        dischargePort: 'Select discharge port',
        directTransit: 'Select direct/transit',
        transitPort: 'Select transit port',
        transitType: 'Select transit type',
        shipCompany: 'Select shipping company',
        contractNo: 'Select contract no',
        spaceStatus: 'Select space status',
        priceStatus: 'Select price trend',
        cargoType: 'Select cargo type',
        vesselSchedule: 'Select vessel schedule',
        voyage: 'Enter voyage',
        freeStorageDays: 'Enter free storage days',
        nac: 'Select NAC',
        validPeriod: 'Select valid period range',
        etd: 'Select ETD range',
        eta: 'Select ETA range',
        vesselName: 'Enter vessel name',
        voyageNo: 'Enter voyage no',
        cutoffDate: 'Select cutoff date range',
        destinationRegion: 'Enter destination region',
        entryPerson: 'Enter created by',
        createDate: 'Select create date range',
        rateModifier: 'Enter rate modifier',
        modifyDate: 'Select modify date range',
        rateStatus: 'Select rate status',
        routeName: 'Select route name',
        routeCodeSelect: 'Select route code',
        terminal: 'Select terminal',
        branch: 'Select branch'
      }
    },

    // Last Mile Rate Filter Fields Internationalization
    lastmileFilterFields: {
      // Labels
      labels: {
        priceMethod: 'Price Method',
        status: 'Rate Status',
        destinationPort: 'Destination Port',
        agent: 'Agent Name',
        currency: 'Currency',
        feeName: 'Fee Name',
        unit: 'Unit',
        addressType: 'Address Type',
        country: 'Country',
        postalCode: 'Postal Code',
        state: 'State',
        city: 'City',
        district: 'District',
        warehouseCode: 'Warehouse Code'
      },

      // Placeholders
      placeholders: {
        priceMethod: 'Select price method',
        status: 'Select rate status',
        destinationPort: 'Select destination port',
        agent: 'Enter agent name',
        currency: 'Select currency',
        feeName: 'Select fee name',
        unit: 'Select unit',
        addressType: 'Select address type',
        country: 'Select country',
        postalCode: 'Select postal code',
        state: 'Select state',
        city: 'Select city',
        district: 'Select district',
        warehouseCode: 'Enter warehouse code'
      }
    },

    // Actions
    actions: {
      combinationQuery: 'Combination Query',
      batchImport: 'Batch Import',
      export: 'Export List',
      customTable: 'Custom Table',
      download: 'Download',
      copy: 'Copy',
      search: 'Search',
      reset: 'Reset',
      viewDetail: 'View Details',
      edit: 'Edit',
      delete: 'Delete',
      enable: 'Enable',
      disable: 'Disable',
      // Rate query specific actions
      query: 'Query',
      advancedQuery: 'Advanced Query',
      simpleQuery: 'Simple Query',
      saveQuery: 'Save Query',
      loadQuery: 'Load Query',
      clearQuery: 'Clear Query',
      exportQuery: 'Export Query Results',
      shareQuery: 'Share Query',
      // Combination query related actions
      startCombinationQuery: 'Start Combination Query',
      stopCombinationQuery: 'Stop Combination Query',
      saveSolution: 'Save Solution',
      compareSolutions: 'Compare Solutions',
      selectBestSolution: 'Select Best Solution',
      // Result processing actions
      viewDetails: 'View Details',
      addToFavorites: 'Add to Favorites',
      removeFromFavorites: 'Remove from Favorites',
      addToCart: 'Add to Cart',
      removeFromCart: 'Remove from Cart',
      batchOperations: 'Batch Operations ({count})',
      batchOperationsAll: 'Batch Operations',
      batchExportExcel: 'Batch Export Excel',
      batchExportCsv: 'Batch Export CSV',
      exportRate: 'Export Rates ({count})',
      exportRateAll: 'Export Rates',
      batchExport: 'Batch Export',
      confirmExportTitle: 'Confirm Export',
      confirmExportContent: 'Do you want to trigger an export task for all rate results under the current query conditions?',
      taskManagerRedirectTitle: 'Export Reminder',
      taskManagerRedirectContent: 'Export task has been triggered successfully, please check the task center later.',
      goToTaskManager: 'View Task',
      quickQuotation: 'Quick Quotation'
    },

    // Quick Quotation
    quickQuotation: {
      title: 'Quick Quotation',
      instruction: 'You can modify/copy the text below for quick quotation',
      priceAdjustment: 'Quickly add price based on original price',
      priceAdjustmentPlaceholder: 'Please enter',
      surcharge: 'Surcharge',
      settings: 'Settings',
      cancel: 'Cancel',
      confirm: 'Text Quotation',
      copySuccess: 'Quotation text copied to clipboard',
      copyFailed: 'Copy failed, please try again',
      // Quotation text fields
      fields: {
        carrier: 'Carrier',
        pol: 'Port of Loading',
        pod: 'Port of Discharge',
        etdEta: 'ETD/ETA',
        vesselVoyage: 'Vessel/Voyage',
        routeCode: 'Route Code',
        directTransit: 'Direct/Transit',
        oceanFreight: 'Ocean Freight',
        otherCharges: 'Other Charges',
        surcharge: 'Surcharge'
      },
      // Custom text settings
      customText: {
        title: 'Custom Text Settings',
        instruction: 'The content set here will be displayed in the generated quotation text',
        enableCustomText: 'Custom Display Text',
        textPosition: 'Text Position',
        positionTop: 'Top',
        positionBottom: 'Bottom',
        customTextPlaceholder: 'Please enter custom text',
        save: 'Save',
        cancel: 'Cancel'
      }
    },

    // Ecommerce Rate Query
    ecommerce: {
      title: 'Ecommerce Rate Query',
      filters: {
        pol: 'Port of Loading',
        pod: 'Port of Discharge',
        eta: 'ETA:',
        containerType: 'Container',
        carrier: 'Carrier',
        direct: 'Direct',
        transit: 'Transit'
      },
      placeholders: {
        pol: 'Select POL',
        pod: 'Select POD',
        containerType: 'Select Container',
        carrier: 'Select Carrier',
        input: 'Please enter',
        weight: 'KG'
      },
      actions: {
        ranking: 'Rate Ranking',
        aiAnalysis: 'AI Analysis',
        pin: 'Pin Rate',
        unpin: 'Unpin',
        replacePin: 'Replace Pin',
        quickQuote: 'Quick Quote',
        priceTrend: 'Price Trend',
        arrival: 'Arrival',
        departure: 'Departure',
        aiAnalysisLabel: 'AI Analysis'
      },
      labels: {
        pinnedRates: 'Pinned Rates',
        day: 'Day',
        receiving: 'Receiving',
        vessel: 'Vessel',
        voyage: 'Voyage',
        basePrice: 'Base',
        totalPrice: 'Total',
        departure: 'Departure',
        arrival: 'Arrival',
        origin: 'Origin',
        destination: 'Destination'
      },
      status: {
        available: 'Available',
        full: 'Full'
      },
      cargoTypes: {
        general: 'General'
      },
      sort: {
        earliest: 'Earliest',
        fastest: 'Fastest',
        cheapest: 'Cheapest'
      },
      columns: {
        id: 'Rate ID',
        carrierName: 'Carrier Name',
        pol: 'POL',
        pod: 'POD',
        transportTerms: 'Terms',
        laneCode: 'Lane Code',
        etd: 'ETD',
        eta: 'ETA',
        transitType: 'Transit Status',
        validFrom: 'Valid From',
        validTo: 'Valid To',
        spaceStatus: 'Space Status',
        cargoType: 'Cargo Type',
        transitTime: 'Transit Time',
        totalPrice: 'Total Rate',
        totalCurrency: 'Total Currency',
        basePrice: 'Base Freight',
        baseCurrency: 'Base Currency',
        vesselName: 'Vessel',
        voyageNo: 'Voyage',
        updateTime: 'Update Time',
        surchargeLabel: 'Surcharge Label',
        surchargeAmount: 'Surcharge Amount',
        cutoffDate: 'Cutoff Date',
        actions: 'Actions'
      },
      freshness: {
        unknown: 'Unknown',
        unknownTime: 'Unknown Time',
        veryFresh: 'Very Fresh',
        fresh: 'Fresh',
        relativelyFresh: 'New',
        withinOneDay: 'Within 1 Day',
        withinTwoDays: 'Within 2 Days',
        stale: 'Stale',
        minutesAgo: 'Updated {minutes}m ago',
        hoursAgo: 'Updated {hours}h ago',
        daysAgo: 'Updated {days}d ago'
      },
      messages: {
        searching: 'Searching...',
        searchFailed: 'Search failed',
        detailNotFound: 'Rate details not found',
        dataNotFound: 'Rate data not found',
        selectRateFirst: 'Please select at least one rate first',
        pinSuccess: 'Pinned {count} rate(s) successfully',
        unpinSuccess: 'Unpinned successfully',
        unpinAllSuccess: 'Unpinned all rates successfully',
        unpinCountSuccess: 'Unpinned {count} rate(s) successfully',
        pinFailed: 'Pin failed',
        unpinFailed: 'Unpin failed'
      },
      quickQuote: {
        title: 'Quick Quote',
        cancel: 'Cancel',
        save: 'Save',
        tips: 'You can modify/copy the text below for a quick quote',
        record: 'Record',
        recordIndex: 'Record {index}',
        surchargeDetail: '[Surcharge Details]',
        surchargeHeader: 'Category    Charge Name    Payment    Unit    Currency    Price',
        totalEstimate: 'Total Amount: {currency} {amount} (Estimated)',
        customText: {
          title: 'Custom Text Settings',
          info: 'The content set here will be displayed in the generated quotation text',
          enable: 'Custom Display Text',
          position: 'Position',
          top: 'Top',
          bottom: 'Bottom',
          default: 'Please find the quotation below. Valid until the end of this month.',
          aiCopy: 'AI Copywriting',
          generate: 'Generate Copy',
          styles: {
            concise: 'Concise',
            professional: 'Professional',
            friendly: 'Friendly'
          }
        },
        additionalFees: 'Surcharge',
        basicOceanFreight: 'Basic Ocean Freight',
        totalRate: 'Total Rate',
        fixedPriceMarkup: 'Fixed Price Markup',
        unifiedMarkup: 'Unified Markup for all containers:',
        baseMarkup: 'Base Markup',
        totalMarkup: 'Total Markup',
        fixedRatioMarkup: 'Fixed Ratio Markup',
        unifiedRatioMarkup: 'Unified Ratio Markup (%):',
        baseRatioMarkup: 'Base Ratio Markup',
        totalRatioMarkup: 'Total Ratio Markup',
        imageQuote: 'Image Quote',
        textQuote: 'Text Quote',
        generatingPreview: 'Generating quote preview...',
        generatingBatchPreview: 'Generating all quote images...',
        aiMarkupSuccess: 'AI batch markup completed',
        copySuccess: 'Copied {count} quote(s) to clipboard',
        quoter: 'Quoted by',
        validity: 'Validity',
        tabs: {
          aiMarkup: 'AI Markup',
          fixedMarkup: 'Fixed Price Markup',
          percentMarkup: 'Fixed Percent Markup'
        },
        labels: {
          allContainerMarkup: 'All Container Markup:',
          allContainerMarkupPercent: 'All Container Markup (%):',
          minPrice: 'Min Price:',
          maxPrice: 'Max Price:',
          and: 'and',
          por: 'Place of Receipt',
          fnd: 'Place of Delivery',
          sailing: 'Sailing/Arrival',
          vesselVoyage: 'Vessel/Voyage',
          markupLimits: 'Markup Limits',
          paymentType: 'Payment Type',
          unit: 'Unit',
          unitBox: 'Per Box'
        },
        columns: {
          type: 'Type',
          basePrice: 'Base Price',
          totalPrice: 'Total Price',
          markupBase: 'Base Markup',
          markupTotal: 'Total Markup',
          markupBasePercent: 'Base Markup %',
          markupTotalPercent: 'Total Markup %',
          originFee: 'Origin Charges',
          destinationFee: 'Destination Charges',
          otherFee: 'Other Fees'
        },
        actions: {
          applyAll: 'Apply All',
          copyAll: 'Copy All',
          exportImage: 'Export Image',
          exportAll: 'Export All',
          startAi: 'Start AI Markup'
        }
      },
      strategy: {
        title: 'AI Markup Strategy',
        balance: 'Balanced (Smart Balance)',
        acquisition: 'Customer First (Low Price)',
        profit: 'Profit First (High Premium)'
      },
      aiAdvice: {
        title: 'Rate Selection Advice',
        subtitle: 'AI Expert Real-time Analysis',
        strongSuggest: 'AI Strongly Recommend',
        suggest: 'AI Recommend',
        shortComment: 'Short Comment',
        reason: 'Reason',
        total: 'Total',
        etd: 'ETD',
        eta: 'ETA'
      },
      aiAssistant: {
        langCode: 'en_US',
        name: 'Rate Analysis Assistant',
        description: 'AI expert for rate details analysis',
        skillPrefix: 'Rate Analysis',
        displayText: 'Perform analysis based on current filters and queried rates.',
        analyzeSingle: 'Please help me analyze if this {carrier} rate from {pol} to {pod} is worth it, and provide selection advice.',
        analyzeAll: 'Please help me analyze the {count} ecommerce rates currently found.'
      },
      ranking: {
        title: 'Rate Ranking',
        back: 'Back',
        hotRoutes: 'Hot Routes',
        hotCarriers: 'Hot Carriers',
        freightIndex: 'Freight Index',
        routeTrend: 'Route Price Trend',
        up: 'Rising',
        down: 'Falling',
        stable: 'Stable',
        day: 'Day',
        week: 'Week',
        etdPlaceholder: ['Earliest ETD', 'Latest ETD'],
        top10Routes: 'TOP 10 Routes',
        top10Carriers: 'TOP 10 Carriers',
        expand: 'Expand',
        collapse: 'Collapse',
        compare: 'Compare',
        multiCompare: 'Multi-Chart Comparison',
        multiCompareTitle: 'Comparison ({count}/6)',
        clear: 'Clear',
        maxCompareWarning: 'Maximum 6 charts allowed for comparison',
        addedToCompare: 'Added to comparison bar',
        chartLabels: {
          frequency: 'Query Frequency (%)',
          avgPrice: 'Avg Price ($)'
        },
        routeLines: {
          asia: 'Asia Line',
          africa: 'Africa Line',
          americaCanada: 'US/Canada Line',
          europeMed: 'Europe/Med Line',
          latinAmerica: 'Latin America Line',
          ausNz: 'AUS/NZ Line'
        },
        originPorts: {
          shanghai: 'Shanghai (CNSHA)',
          ningbo: 'Ningbo (CNNGB)',
          shenzhen: 'Shenzhen (CNSZX)'
        },
        queryCount: 'Query Count',
        avgHeat: 'Avg Heat',
        avgPrice: 'Avg Price',
        baseWoWRate: 'Base WoW Rate',
        totalWoWRate: 'Total WoW Rate',
        routeTrendShort: 'Route Trend',
        daily: 'Daily',
        weekly: 'Weekly',
        close: 'Close',
        addToCompare: 'Add to Compare'
      },
      detailModal: {
        boxPrices: 'Unit Prices Summary',
        costDetail: 'Cost Detail',
        refundFees: 'Refund & Changes',
        dnd: 'DND',
        priceTrend: 'Price Trend',
        priceTrendTip: 'Rate trend by ETD for the same lane, carrier and container type',
        totalEstimate: 'Estimated Total',
        includes: 'Includes {categories}',
        tips1: 'Due to potential changes in carrier policies, the above fees are for reference only.',
        tips2: 'DND free time is subject to actual carrier rules.',
        hint: 'Hint',
        totalLogicHint: 'Total calculation logic: Ocean Freight + Origin Charges (Main Currency) + Prepaid Destination Charges (Main Currency). (Main Currency: Currency of basic ocean freight)',
        weightHint: 'Default weight per container: 12,000 KG',
        feeName: 'Fee Item',
        currency: 'Currency',
        amount: 'Amount',
        base: 'Base',
        total: 'Total',
        validityPeriod: 'Validity period(day)',
        costPerDay: 'Cost per day',
        oceanBase: 'Ocean Base',
        oceanExtra: 'Ocean Extra',
        free: 'Free',
        categories: {
          OCEAN_BASE: 'Ocean Base',
          OCEAN_EXTRA: 'Ocean Extra',
          POR: 'Origin Surcharges',
          FND: 'Destination Surcharges',
          OTHERS: 'Other Fees',
          Freight: 'Ocean Freight',
          Origin: 'Origin Charges',
          Destination: 'Destination Charges',
          Others: 'Others'
        }
      },
      charts: {
        performanceTitle: 'Carrier Value Analysis',
        positionTitle: 'Market Rate Distribution',
        currentItem: 'Current Item',
        valueHigh: 'High Value',
        regularRecommend: 'Regular',
        boundaryLine: 'Value Boundary',
        boundaryTip: 'Note: Dashed line is the value boundary. Points in bottom-left represent lower price for same transit, or shorter transit for same price.',
        average: 'Average',
        median: 'Median (P50)',
        stdDev: 'Std Dev',
        marketDist: 'Market Distribution',
        percentile: 'Market Percentile',
        max: 'Max',
        min: 'Min',
        countUnit: 'Records'
      }
    },

    // FCL Rate Query
    fcl: {
      title: 'FCL Rate Query',
      actions: {
        combinationQuery: 'Combination Query'
      },
      messages: {
        viewDetailFailed: 'Cannot view details: Missing record ID',
        inquiryRequired: 'Inquiry Required',
        validFrom: 'Until {endDate}',
        validTo: 'From {beginDate}',
        unknown: 'Unknown',
        noData: '-'
      },
      // Status enums
      enums: {
        spaceStatus: {
          smooth: 'Smooth',
          normal: 'Normal',
          singleApply: 'Single Apply',
          overbooked: 'Overbooked',
          notAccepting: 'Not Accepting',
          limited: 'Limited',
          suspended: 'Suspended',
          inquiry: 'Inquiry'
        },
        priceTrends: {
          rising: 'Rising',
          falling: 'Falling',
          stable: 'Stable'
        },
        status: {
          draft: 'Draft',
          normal: 'Normal',
          expired: 'Expired',
          offline: 'Offline'
        },
        rateTypes: {
          contract: 'Contract',
          spot: 'SPOT E-commerce'
        },
        cargoTypes: {
          general: 'General',
          dangerous: 'Dangerous',
          frozen: 'Frozen',
          special: 'Special',
          steelCoil: 'Steel Coil',
          liquid: 'Liquid',
          chemical: 'Chemical',
          textile: 'Textile'
        },
        transitTypes: {
          direct: 'Direct',
          transit: 'Transit'
        }
      },
      // Table column configuration
      tableColumns: {
        priceNo: 'Rate No.',
        pol: 'Port of Loading',
        pod: 'Port of Discharge',
        carrierId: 'Carrier',
        status: 'Status'
      },

      // Detail page configuration
      detail: {
        // Field labels
        fields: {
          voyage: 'Voyage',
          carrier: 'Carrier',
          contractNo: 'Contract No.',
          nac: 'NAC',
          freeContainerDays: 'Free Container Days',
          freeStorageDays: 'Free Storage Days',
          validPeriod: 'Valid Period',
          overweightRemarks: 'Overweight Remarks',
          receivingInstructions: 'Special Receiving Instructions',
          remarks: 'Remarks'
        },
        // Placeholders
        placeholders: {
          inputVoyage: 'Enter voyage (days)',
          selectCarrier: 'Select carrier',
          selectContractNo: 'Select contract number',
          selectNac: 'Select NAC',
          inputFreeContainerDays: 'Enter free container days',
          inputFreeStorageDays: 'Enter free storage days',
          startDate: 'Start date',
          endDate: 'End date',
          inputOverweightRemarks: 'Enter overweight remarks',
          inputReceivingInstructions: 'Enter special receiving instructions',
          inputRemarks: 'Enter remarks'
        },
        // Messages
        messages: {
          selectCarrier: 'Please select carrier',
          selectValidPeriod: 'Please select valid period',
          selectRateType: 'Please select rate type',
          selectDeparturePort: 'Please select port of loading',
          selectDischargePort: 'Please select port of discharge',
          saveFirst: 'Please save data first',
          loadDataFailed: 'Failed to load data',
          copyDataLoadFailed: 'Failed to load copy data',
          invalidApiResponse: 'Invalid API response format',
          invalidCopyApiResponse: 'Invalid copy data API response format',
          unknownError: 'Unknown error',
          missingFeeId: 'Missing fee ID',
          containerSettingsUpdated: 'Container settings updated'
        },
        // Section titles
        sections: {
          ddSettings: 'D&D'
        },
        // Modals
        modals: {
          selectContainerTypes: 'Select container types to display in the rate table'
        },
        // Page titles
        pageTitles: {
          create: 'Create FCL Rate',
          edit: 'Edit FCL Rate',
          view: 'Rate Details'
        },
        // Rate details
        rateDetails: {
          nonContainerBilling: 'Non-container Billing'
        },
        // Weekday configuration
        weekdays: {
          monday: 'Monday',
          tuesday: 'Tuesday',
          wednesday: 'Wednesday',
          thursday: 'Thursday',
          friday: 'Friday',
          saturday: 'Saturday',
          sunday: 'Sunday'
        }
      }
    },

    // Pre-port Rate Query
    preport: {
      title: 'Pre-port Rate Query',
      actions: {
        combinationQuery: 'Combination Query'
      },
      messages: {
        viewDetailFailed: 'Cannot view details: Missing record ID',
        inquiryRequired: 'Inquiry Required',
        validFrom: 'Until {endDate}',
        validTo: 'From {beginDate}',
        unknown: 'Unknown',
        noData: '-'
      },
      // Status enums
      enums: {
        status: {
          draft: 'Draft',
          normal: 'Normal',
          expired: 'Expired',
          offline: 'Offline'
        },
        priceTypes: {
          contract: 'Contract',
          spot: 'SPOT E-commerce'
        },
        cargoTypes: {
          general: 'General',
          dangerous: 'Dangerous',
          frozen: 'Frozen',
          special: 'Special'
        }
      }
    },

    // Last Mile Rate Query
    lastmile: {
      tableConfigName: 'Last Mile Rate Query Table Configuration',
      title: 'Last Mile Rate Query',
      actions: {
        combinationQuery: 'Combination Query'
      },
      messages: {
        viewDetailFailed: 'Cannot view details: Missing record ID',
        inquiryRequired: 'Inquiry Required',
        validFrom: 'Until {endDate}',
        validTo: 'From {beginDate}',
        unknown: 'Unknown',
        noData: '-'
      },
      // Status enums
      enums: {
        status: {
          draft: 'Draft',
          normal: 'Normal',
          expired: 'Expired',
          offline: 'Offline'
        },
        priceTypes: {
          contract: 'Contract',
          spot: 'SPOT E-commerce'
        },
        addressTypes: {
          warehouse: 'Warehouse',
          port: 'Port',
          city: 'City',
          district: 'District'
        }
      }
    },

    // Custom Table
    customTable: {
      title: 'Table Settings'
    },

    // Combination Query
    combination: {
      title: 'Combination Solution Query',
      description: 'Query multiple rate combination solutions',
      // Page Header
      header: {
        back: 'Back',
        pageTitle: 'Combination Solution Query',
        portPrice: 'Port Price',
        trunkPrice: 'Trunk Price',
        lastMilePrice: 'Last Mile Price'
      },
      // Steps
      steps: {
        step1: {
          number: '1',
          title: 'Fill Query Conditions',
          description: 'Configure query parameters'
        },
        step2: {
          number: '2',
          title: 'Select Matching Rates',
          description: 'Choose suitable solutions'
        }
      },
      // Query Form
      form: {
        basicInfo: {
          title: 'Basic Information',
          directType: 'Direct/Transit',
          directTypeOptions: {
            notSpecified: 'Not Specified',
            direct: 'Direct',
            transit: 'Transit'
          },
          route: 'Route',
          routePlaceholder: 'Please select route',
          carrier: 'Carrier',
          carrierPlaceholder: 'Please select carrier',
          cargoType: 'Cargo Type',
          cargoTypePlaceholder: 'Please select cargo type'
        },
        addressInfo: {
          title: 'Address Information',
          pol: 'Port of Loading',
          polPlaceholder: 'Please select port of loading',
          pod: 'Port of Discharge',
          podPlaceholder: 'Please select port of discharge',
          pot: 'Port of Transit',
          potPlaceholder: 'Please select port of transit'
        },
        containerInfo: {
          title: 'Container Type & Quantity',
          containerType: 'Container Type',
          containerTypePlaceholder: 'Please select container type',
          quantity: 'Quantity',
          quantityPlaceholder: 'Please enter quantity',
          addContainer: 'Add Container',
          removeContainer: 'Remove Container'
        },
        timeInfo: {
          title: 'Time Information',
          etd: 'ETD',
          etdPlaceholder: 'Please select ETD',
          eta: 'ETA',
          etaPlaceholder: 'Please select ETA'
        },
        buttons: {
          search: 'Search',
          reset: 'Reset',
          next: 'Next',
          back: 'Previous'
        }
      },
      // Query Results
      results: {
        title: 'Query Results',
        summary: 'Query Summary',
        summaryItems: {
          route: 'Route',
          pol: 'Port of Loading',
          pod: 'Port of Discharge',
          carrier: 'Carrier',
          cargoType: 'Cargo Type',
          containers: 'Container Type & Quantity'
        },
        noResults: 'No matching combination solutions found',
        loading: 'Querying...',
        totalCount: 'Found {count} combination solutions',
        // Rate Type Labels
        rateTypes: {
          portPrice: 'Port Price',
          trunkPrice: 'Trunk Price',
          lastMilePrice: 'Last Mile Price'
        },
        // Status Labels
        status: {
          loading: 'Loading',
          loaded: 'Loaded',
          error: 'Error'
        }
      },
      // Messages
      messages: {
        searchSuccess: 'Query completed',
        searchFailed: 'Query failed',
        noDataFound: 'No matching rate data found',
        selectRateType: 'Please select at least one rate type',
        fillRequiredFields: 'Please fill in required fields',
        invalidFormData: 'Invalid form data, please check input',
        // Rate loading related
        trunkRatesLoaded: 'Trunk rate data loaded successfully',
        portRatesLoaded: 'Port rate data loaded successfully',
        lastMileRatesLoaded: 'Last mile rate data loaded successfully',
        // Error messages
        loadTrunkRatesFailed: 'Failed to load trunk rate data',
        loadPortRatesFailed: 'Failed to load port rate data',
        loadLastMileRatesFailed: 'Failed to load last mile rate data',
        // Validation messages
        atLeastOneRateType: 'Please select at least one price type (Port Price, Trunk Price, or Last Mile Price)',
        atLeastOneContainer: 'Please add at least one container',
        selectContainerType: 'Please select container type for row {index}',
        inputContainerQuantity: 'Please enter quantity for row {index}',
        atLeastOneRate: 'Please select at least one rate solution',
        combinationGenerated: 'Combination solution generated successfully, selected {count} rate solutions'
      },
      // Address related
      address: {
        loadingPoint: {
          title: 'Loading Point',
          administrative: 'Administrative Division',
          province: 'Please select province',
          city: 'Please select city',
          district: 'Please select district',
          detail: 'Detailed Location',
          street: 'Please select street',
          community: 'Please select community',
          detailAddress: 'Detailed Address',
          detailAddressPlaceholder: 'Please enter detailed address'
        },
        delivery: {
          title: 'Last Mile Delivery Address',
          addressType: 'Address Type',
          thirdParty: 'Third Party Address',
          amazonWarehouse: 'Amazon Warehouse',
          yicang: 'Yicang',
          countryPostcode: 'Country & Postal Code',
          selectCountry: 'Please select country',
          inputPostcode: 'Please enter postal code',
          stateCityRegion: 'State, City & Region',
          selectState: 'Please select state',
          selectCity: 'Please select city',
          selectRegion: 'Please select region',
          detailAddress: 'Detailed Address',
          detailAddressPlaceholder: 'Please enter detailed address',
          warehouseCode: 'Warehouse Code',
          selectAmazonWarehouse: 'Please select Amazon warehouse',
          selectYicang: 'Please select Yicang'
        }
      },
      // Table related
      table: {
        trunkRates: 'Trunk Rate Matching Results',
        portRates: 'Port Rate Matching Results',
        lastMileRates: 'Last Mile Rate Matching Results',
        clickToSelect: 'Click row to select rate',
        containerInfo: 'Container Type & Quantity Information',
        close: 'Close',
        copyText: 'Copy Text'
      },
      // Modal related
      modal: {
        exportQuotation: 'Export Quotation',
        quickQuotation: 'Quick Quotation Text',
        quotationDescription: 'The following is the quotation text generated based on your selected container types and quantities. You can copy and send it to customers:',
        quotationTitle: 'Combination Solution Quotation',
        queryTime: 'Query Time',
        transportType: 'Transport Type',
        containerInfo: 'Container Type & Quantity',
        pol: 'Port of Loading',
        pod: 'Port of Discharge',
        carrier: 'Carrier',
        trunkRate: 'Trunk Rate',
        portRate: 'Port Rate',
        lastMileRate: 'Last Mile Rate',
        selected: 'Selected',
        note: 'Note: This quotation is based on current query conditions, actual rates may vary',
        contactService: 'For detailed quotations, please contact customer service'
      },
      // Status and enums
      enums: {
        // Cargo types
        cargoTypes: {
          fcl: 'FCL',
          lcl: 'LCL',
          air: 'Air Freight'
        },
        // Container types
        containerTypes: {
          '20gp': '20GP',
          '40gp': '40GP',
          '40hc': '40HC'
        },
        // Space status
        spaceStatus: {
          sufficient: 'Sufficient',
          tight: 'Tight',
          unknown: 'Unknown'
        },
        // Price trend
        priceTrend: {
          rising: 'Rising',
          falling: 'Falling',
          stable: 'Stable',
          unknown: 'Unknown'
        },
        // Rate status
        rateStatus: {
          valid: 'Valid',
          invalid: 'Invalid',
          pending: 'Pending',
          unknown: 'Unknown'
        },
        // Freight type
        freightType: {
          contract: 'Contract',
          spot: 'SPOT E-commerce'
        },
        // Direct flag
        directFlag: {
          direct: 'Direct',
          transit: 'Transit'
        },
        // Price method
        priceMethod: {
          direct: 'Direct',
          branchLine: 'Branch Line',
          seaRail: 'Sea-Rail'
        },
        // Branch line type
        branchLine: {
          haiding: 'Haiding Branch Line',
          zhapu: 'Zhapu Branch Line',
          huzhou: 'Huzhou Sea-Rail',
          yiwu: 'Yiwu Sea-Rail'
        },
        // Delivery address type
        deliveryAddressType: {
          thirdParty: 'Third Party Address',
          amazon: 'Amazon Warehouse',
          yicang: 'Yicang',
          unknown: 'Unknown'
        },
        // Status
        status: {
          draft: 'Draft',
          normal: 'Normal',
          expired: 'Expired',
          offline: 'Offline',
          unknown: 'Unknown'
        }
      },
      // Buttons and actions
      actions: {
        next: 'Next',
        back: 'Back to Previous',
        search: 'Search',
        reset: 'Reset',
        printQuotation: 'Print Quotation',
        generateCombination: 'Generate Combination',
        exportQuotation: 'Export Quotation',
        copyQuotationText: 'Copy Quotation Text'
      },
      // Preview card
      card: {
        trunkRate: 'Trunk Rate: {priceNo}',
        portRate: 'Port Rate: {priceNo}',
        lastMileRate: 'Last Mile Rate: {priceNo}',
        viewDetail: 'View Detail'
      }
    },

    // Messages
    messages: {
      searchSuccess: 'Search completed',
      tableSettingsApplied: 'Table settings applied',
      combinationQueryPending: 'Combination query function under development...',
      batchImportPending: 'Batch import function under development...',
      exportPending: 'Export function under development...',
      clickTabToLoad: 'Click "{tab}" tab to load content',
      lclPending: 'LCL rate management function under development...',
      lclFeatures: 'Will support LCL rate CRUD operations, batch operations and other functions',
      airPending: 'Air freight rate management function under development...',
      airFeatures: 'Will support air freight rate CRUD operations, batch operations and other functions',
      // General messages
      loading: 'Loading...',
      loadSuccess: 'Load successful',
      loadFailed: 'Load failed',
      saveSuccess: 'Save successful',
      saveFailed: 'Save failed',
      deleteSuccess: 'Delete successful',
      deleteFailed: 'Delete failed',
      operationSuccess: 'Operation successful',
      operationFailed: 'Operation failed',
      // Confirmation messages
      confirmDelete: 'Are you sure you want to delete this record?',
      confirmEnable: 'Are you sure you want to enable this record?',
      confirmDisable: 'Are you sure you want to disable this record?',
      confirmBatchDelete: 'Are you sure you want to delete the selected {count} records?',
      confirmBatchEnable: 'Are you sure you want to enable the selected {count} records?',
      confirmBatchDisable: 'Are you sure you want to disable the selected {count} records?',
      // Validation messages
      requiredField: 'This field is required',
      invalidFormat: 'Invalid format',
      invalidEmail: 'Invalid email format',
      invalidPhone: 'Invalid phone number format',
      invalidDate: 'Invalid date format',
      // Rate query specific messages
      queryInProgress: 'Query in progress, please wait...',
      queryCompleted: 'Query completed',
      queryNoResults: 'No matching rate data found',
      queryError: 'An error occurred during query',
      // Combination query related
      combinationQueryStarted: 'Combination solution query started',
      combinationQueryCompleted: 'Combination solution query completed',
      combinationQueryFailed: 'Combination solution query failed',
      // Rate type selection
      selectRateTypes: 'Please select rate types to query',
      atLeastOneRateType: 'At least one rate type must be selected',
      // Query condition validation
      invalidQueryConditions: 'Invalid query conditions, please check input',
      missingRequiredFields: 'Missing required fields, please complete query conditions',
      // Data loading status
      dataLoading: 'Loading data...',
      dataLoaded: 'Data loaded successfully',
      dataLoadFailed: 'Failed to load data',
      // Pagination related
      pageInfo: 'Page {current} of {total}',
      pageSizeChanged: 'Showing {size} items per page',
      // Sorting related
      sortBy: 'Sort by',
      sortAsc: 'Ascending',
      sortDesc: 'Descending',
      // Filter related
      filterApplied: 'Filter applied',
      filterCleared: 'Filter cleared',
      filterReset: 'Filter reset'
    }
  },


    // Export
    export: {
      filename: 'Inquiry Data'
    },

    // Detail page
    detail: {
      postalCode: 'Postal Code',
      stuffingTerminal: 'Terminal / CY',
      stuffingTerminalPlaceholder: 'Select terminal / CY',
      deliveryTerminal: 'Arrival Terminal / CY',
      deliveryTerminalPlaceholder: 'Select terminal / CY',
      stuffingCountry: 'Country',
      stuffingProvince: 'Province / State',
      stuffingCity: 'City',
      stuffingDistrict: 'District / County',
      stuffingStreet: 'Street / Town',
      stuffingCommunity: 'Community',
      stuffingSection: 'Pre-carriage Stuffing Point',
      detailAddress: 'Detail Address',
      assignType: 'Backfill Target'
    },


  // Rate Management Module
  rateManagement: {
    // Module title and description
    title: 'Rate Management',
    description: 'Manage various rate information',

    // Tab navigation
    tabs: {
      ecommerce: 'E-commerce Rates',
      fcl: 'FCL Rates',
      lcl: 'LCL Rates',
      air: 'Air Freight Rates',
      preport: 'Preport Rates',
      lastmile: 'Last Mile Rates'
    },

    // FCL Rate Management
    fcl: {
      // Page titles
      title: 'FCL Rate Management',
      description: 'Manage FCL rate information',
      createTitle: 'Create FCL Rate',
      editTitle: 'Edit FCL Rate',
      viewTitle: 'View FCL Rate Details',

      // Filter conditions
      filters: {
        // Basic information filters
        priceNo: 'Rate No.',
        pol: 'Port of Loading',
        pod: 'Port of Discharge',
        pot: 'Transit Port',
        pot1: 'Transit Port (1st)',
        pot2: 'Transit Port (2nd)',
        pot3: 'Transit Port (3rd)',
        carrierId: 'Carrier',
        contractNo: 'Contract No.',
        laneId: 'Route',

        // Status filters
        spaceStatus: 'Space Status',
        status: 'Rate Status',
        priceTrend: 'Price Trend',

        // Cargo information filters
        cargoType: 'Cargo Type',

        // Schedule information filters
        weekdays: 'Sailing Schedule',
        vessel: 'Vessel Name',
        voyage: 'Voyage No.',
        voyageDays: 'Transit Days',

        // Time filters
        beginDate: 'Effective Date',
        endDate: 'Expiry Date',
        etd: 'ETD',
        eta: 'ETA',
        dateCreated: 'Created Date',
        dateModified: 'Modified Date',

        // Other filters
        detentionFreeDays: 'Free Detention Days',
        demurrageFreeDays: 'Free Demurrage Days',
        nac: 'NAC',
        remarks: 'Remarks',
        createdBy: 'Created By',
        modifiedBy: 'Modified By'
      },

      // Placeholders
      placeholders: {
        priceNo: 'Enter rate number',
        pol: 'Select port of loading',
        pod: 'Select port of discharge',
        pot: 'Select transit port',
        carrierId: 'Select carrier',
        contractNo: 'Select contract number',
        laneId: 'Select route',
        spaceStatus: 'Select space status',
        status: 'Select rate status',
        priceTrend: 'Select price trend',
        cargoType: 'Select cargo type',
        weekdays: 'Select sailing schedule',
        vessel: 'Enter vessel name',
        voyage: 'Enter voyage number',
        voyageDays: 'Enter transit days',
        detentionFreeDays: 'Enter free detention days',
        demurrageFreeDays: 'Enter free demurrage days',
        nac: 'Select NAC',
        beginDate: 'Select effective date range',
        endDate: 'Select expiry date range',
        etd: 'Select ETD range',
        eta: 'Select ETA range',
        remarks: 'Enter remarks',
        createdBy: 'Enter creator name',
        dateCreated: 'Select creation date range',
        modifiedBy: 'Enter modifier name',
        dateModified: 'Select modification date range'
      },

      // Table columns
      columns: {
        // Basic information columns
        priceNo: 'Rate Number',
        freightType: 'Rate Type',
        por: 'Place of Receipt',
        pol: 'Port of Loading',
        pdl: 'Place of Delivery',
        pod: 'Port of Destination',
        laneId: 'Route',
        laneCode: 'Route Code',
        pot: 'Port of Transit',
        pot1: 'Transit Port (1st)',
        pot2: 'Transit Port (2nd)',
        pot3: 'Transit Port (3rd)',
        directFlag: 'Direct/Transit',
        directFlag2: 'Direct/Transit',
        carrierId: 'Carrier',
        contractNo: 'Contract No',

        // Status columns
        spaceStatus: 'Space Status',
        priceTrend: 'Price Trend',
        status: 'Status',

        // Cargo information columns
        cargoType: 'Cargo Type',

        // Schedule information columns
        shippingSchedule: 'Shipping Schedule',
        voyage: 'Voyage',
        vesselName: 'Vessel Name',
        voyageNo: 'Voyage No',

        // Time information columns
        validPeriod: 'Valid Period',
        etd: 'ETD',
        eta: 'ETA',
        cutoffDate: 'Cutoff Date',
        beginDate: 'Effective Date',
        endDate: 'Expiry Date',
        createDate: 'Create Date',
        modifyDate: 'Modify Date',

        // Fee information columns
        freeStorageDays: 'Free Storage Days',
        demurrageFreeDays: 'Free Demurrage',
        nac: 'NAC',

        // Container price columns
        gp20: '20GP',
        gp40: '40GP',
        hc20: '20HC',
        hc40: '40HC',
        hc45: '45HC',
        nor20: '20NOR',
        nor40: '40NOR',
        tk20: '20TK',
        tk40: '40TK',
        ot20: '20OT',
        ot40: '40OT',
        fr20: '20FR',
        fr40: '40FR',
        hq40: '40HQ',
        hq45: '45HQ',
        rf20: '20RF',
        rf40: '40RF',

        // Other information columns
        destinationRegion: 'Destination Region',
        entryPerson: 'Created By',
        rateModifier: 'Rate Modifier',
        rateStatus: 'Status',
        routeName: 'Route Name',
        routeCode: 'Route Code',
        terminal: 'Terminal',
        branch: 'Branch',
        remarks: 'Remarks',
        dateCreated: 'Created Date',
        actions: 'Actions'
      },

      // Weekdays
      weekdays: {
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday'
      },

      // Action buttons
      actions: {
        add: 'Add FCL Rate',
        import: 'Import',
        downloadTemplate: 'Download template',
        aiRecognition: 'AI Recognition',
        batchOperations: 'Batch Operations',
        batchOnShelf: 'Batch Online',
        batchOffShelf: 'Batch Offline',
        batchPriceChange: 'Batch Price Change',
        batchValidityChange: 'Batch Time Change',
        batchDelete: 'Batch Delete',
        view: 'Details',
        edit: 'Edit',
        more: 'More',
        copy: 'Copy',
        onShelf: 'Online',
        offShelf: 'Offline',
        delete: 'Delete'
      },

      // Enum values
      enums: {
        // Rate types
        rateTypes: {
          contract: 'Contract Rate',
          spot: 'SPOT E-commerce'
        },

        // Transit types
        transitTypes: {
          direct: 'Direct',
          transit: 'Transit'
        },

        // Space status
        spaceStatus: {
          smooth: 'Smooth',
          normal: 'Normal',
          singleApply: 'Single Apply',
          overbooked: 'Overbooked',
          notAccepting: 'Not Accepting',
          limited: 'Limited',
          suspended: 'Suspended',
          inquiry: 'Inquiry'
        },

        // Price trends
        priceTrends: {
          rising: 'Price Rising',
          falling: 'Price Falling',
          stable: 'Price Stable'
        },

        // Rate status
        status: {
          draft: 'Draft',
          normal: 'Normal',
          expired: 'Expired',
          offline: 'Offline'
        },

        // Cargo types
        cargoTypes: {
          general: 'General Cargo',
          dangerous: 'Dangerous Goods',
          frozen: 'Frozen Goods',
          special: 'Special Container',
          steelCoil: 'Steel Coil',
          liquid: 'Liquid',
          chemical: 'Chemical',
          textile: 'Textile'
        }
      },

      // Batch operations
      batch: {
        selected: 'Selected {count} items',
        onShelfConfirm: 'Are you sure to put {count} rates online?',
        offShelfConfirm: 'Are you sure to put {count} rates offline?',
        deleteConfirm: 'Are you sure to delete {count} rates? This action cannot be undone.',
        onShelfSuccess: 'Successfully put {count} rates online',
        offShelfSuccess: 'Successfully put {count} rates offline',
        deleteSuccess: 'Successfully deleted {count} rates',

        // Batch price change
        priceChange: {
          title: 'Batch Price Change',
          selectedRates: 'Selected {count} rates',
          operationReminder: 'Operation Reminder',
          reminderContent: 'This operation will modify the fee amounts of all selected rates. Please proceed with caution. You can choose specific fee types for batch modification. Leaving empty means the container type price remains unchanged. If the original rate does not have a corresponding container type price, the adjustment will not take effect. Please first choose whether to increase or decrease the price, and always enter positive integers in the input boxes.',
          selectCharge: 'Select Charge',
          selectChargeDesc: 'Select the type of charge to modify prices in batch, such as ocean freight, operation fee, etc.',
          selectChargePlaceholder: 'Select the charge type to modify',
          selectPriceMode: 'Select Price Change Mode',
          priceIncrease: 'Price Increase',
          priceDecrease: 'Price Decrease',
          setPriceMode: 'Fixed Amount',
          priceIncreaseDesc: 'Select price increase mode, enter the increase amount (positive integer) in the input box',
          priceDecreaseDesc: 'Select price decrease mode, enter the decrease amount (positive integer) in the input box',
          setPriceDesc: 'Select fixed amount mode, enter the price of any container type (positive integer)',
          setPrice: 'Set Price',
          selectChargeFirst: 'Please select charge type first, then set price',
          rateListToModify: 'Rate list to be modified',
          rateDetailsList: 'Rate list to be modified',
          rateNo: 'Rate No.',
          pol: 'Port of Loading',
          pod: 'Port of Discharge',
          carrier: 'Carrier',
          status: 'Status',
          showChargePrice: '(Show {charge} price)',
          showAllCharges: '(Show total price of all container-based charges)',
          noData: 'No data',
          selectedSummary: 'Selected {count} rates, will batch modify {type} prices.',
          currentChargeType: 'Current selected charge type: ',
          confirmOperation: 'This operation will modify the ',
          confirmOperationSuffix: ' amount of selected rates, please confirm data accuracy',
          selectChargeAndPrice: 'Please select charge type and set price first, then confirm modification',
          confirm: 'Confirm',
          cancel: 'Cancel'
        },

        // Batch time change
        timeChange: {
          title: 'Batch Time Change',
          operationAlert: 'This operation will batch modify the time information of selected rates, please operate with caution',
          etdTab: 'ETD (Estimated Time of Departure)',
          etaTab: 'ETA (Estimated Time of Arrival)',
          validDateTab: 'Validity Period',
          setNewEtd: 'Set New ETD (Estimated Time of Departure)',
          setNewEta: 'Set New ETA (Estimated Time of Arrival)',
          setNewValidDate: 'Set New Validity Period',
          newEtd: 'New ETD:',
          newEta: 'New ETA:',
          validPeriod: 'Validity Period:',
          selectEtd: 'Select estimated departure date',
          selectEta: 'Select estimated arrival date',
          startDate: 'Start Date',
          endDate: 'End Date',
          currentEtd: 'Current ETD',
          currentEta: 'Current ETA',
          currentValidPeriod: 'Current Validity Period',
          rateListToModify: 'Rates to be modified',
          selectedSummary: 'Selected {count} rates, will batch modify {type} information',
          cancel: 'Cancel',
          confirm: 'Confirm',
          etd: 'ETD',
          eta: 'ETA',
          validDate: 'Validity Period',
          // New fields
          operationReminder: 'Operation Reminder',
          reminderContent: 'This operation will modify the time information of all selected rates, please operate with caution.',
          setNewEtdTitle: 'Set New ETD',
          setNewEtaTitle: 'Set New ETA',
          setNewValidDateTitle: 'Set New Validity Period',
          etdLabel: 'ETD',
          etaLabel: 'ETA',
          validDateLabel: 'Validity Period',
          selectEtdPlaceholder: 'Please select ETD',
          selectEtaPlaceholder: 'Please select ETA',
          startDatePlaceholder: 'Start Date',
          endDatePlaceholder: 'End Date',
          selectedRatesList: 'Rates to be modified',
          // New success messages
          etdUpdateSuccess: 'Successfully modified ETD for {count} rates',
          etaUpdateSuccess: 'Successfully modified ETA for {count} rates',
          validDateUpdateSuccess: 'Successfully modified validity period for {count} rates',
          updateFailed: 'Batch time modification failed'
        },
        // Validity period modification
        validityChange: {
          title: 'Batch Modify Validity Period',
          validityPeriod: 'Validity Period',
          selectValidityPeriod: 'Please select validity period',
          startDate: 'Start Date',
          endDate: 'End Date'
        },
        // Batch operation confirmation
        confirm: {
          online: 'Confirm Batch Publish',
          offline: 'Confirm Batch Unpublish',
          delete: 'Confirm Batch Delete',
          onlineContent: 'Are you sure to publish the selected {count} rates?',
          offlineContent: 'Are you sure to unpublish the selected {count} rates?',
          deleteContent: 'Are you sure to delete the selected {count} rates? This action cannot be undone.'
        },
        // Batch operation messages
        messages: {
          onlineSuccess: 'Batch publish successful',
          onlineFailed: 'Batch publish failed',
          offlineSuccess: 'Batch unpublish successful',
          offlineFailed: 'Batch unpublish failed',
          deleteSuccess: 'Batch delete successful',
          deleteFailed: 'Batch delete failed',
          validityChangeSuccess: 'Batch validity period modification successful',
          validityChangeFailed: 'Batch validity period modification failed',
          selectRatesFirst: 'Please select rates to {action} first',
          selectRatesToOnline: 'Please select rates to publish first',
          selectRatesToOffline: 'Please select rates to unpublish first',
          selectRatesToModify: 'Please select rates to modify first',
          selectRatesToDelete: 'Please select rates to delete first',
          onlyDraftCanOnline: 'Only draft status rates can be published, current selection includes: {status} status rates',
          onlyNormalCanOffline: 'Only normal status rates can be unpublished, current selection includes: {status} status rates',
          onlyDraftCanModify: 'Only draft status rates can be modified, please reselect',
          onlyDraftCanDelete: 'Only draft status rates can be deleted, current selection includes: {status} status rates'
        }
      },

      // Charge items
      chargeItems: {
        oceanFreight: 'Ocean Freight',
        operationFee: 'Operation Fee',
        documentationFee: 'Documentation Fee',
        portCharges: 'Port Charges',
        storageFee: 'Storage Fee',
        inlandTransport: 'Inland Transport',
        baf: 'BAF',
        caf: 'CAF',
        deliveryFee: 'Delivery Fee',
        detentionFee: 'Detention Fee',
        demurrageFee: 'Demurrage Fee',
        additionalFee: 'Additional Fee',
        fuelFee: 'Fuel Fee',
        bookingFee: 'Booking Fee',
        inspectionFee: 'Inspection Fee',
        trailerFee: 'Trailer Fee'
      },

      // AI Recognition
      aiRecognition: {
        title: 'AI Smart Recognition',
        uploadTitle: 'Upload Contract File',
        uploadDesc: 'Click or drag files to this area, the system will automatically recognize content and extract rate information',
        processing: 'AI is recognizing...',
        processingDesc: 'Please wait, AI is analyzing your rate file...',
        success: 'Recognition successful!',
        successDesc: 'Rate information has been successfully recognized and imported',
        error: 'Recognition failed',
        errorDesc: 'File recognition failed, please check file format or re-upload',
        retry: 'Re-upload',
        // AI Recognition Description
        aiDescription: 'AI Recognition Description',
        aiFeatures: [
          'AI will automatically recognize port, carrier, price and other information in rate tables',
          'Supports multiple file formats and complex table structures',
          'After recognition is complete, it will create background tasks for processing',
          'Please ensure uploaded files are clear and readable'
        ],
        // Success messages
        successTitle: 'AI Recognition Task Submitted',
        successDescription: 'The current task is running in the background, please go to <strong>System Settings - Task Management</strong> to view task progress and results.',
        goToTaskManagement: 'Go to Task Management',
        close: 'Close',
        // Upload related
        uploadFile: 'Upload {type} File',
        clickOrDrag: 'Click or drag files to this area to upload',
        supportedFormats: 'Supported formats: PDF, Excel, Word, images, etc.',
        selectFile: 'Select File',
        aiParsing: 'AI Parsing...',
        aiParsingTip: 'AI is parsing the document, please wait...'
      },

      // Detail page
      detail: {
        // Page titles
        pageTitles: {
          create: 'Create FCL Rate',
          edit: 'Edit FCL Rate',
          view: 'Rate Details',
          copy: 'Copy FCL Rate'
        },

        // Button texts
        buttons: {
          back: 'Back',
          saveDraft: 'Save Draft',
          save: 'Save',
          directOnShelf: 'Direct Online',
          aiRecognition: 'AI Recognition',
          edit: 'Edit',
          copy: 'Copy'
        },

        // Copy mode alerts
        copyMode: {
          alert: 'Copying rate information, rate number will be automatically regenerated, please confirm all information before saving'
        },

        // Rate status
        status: {
          label: 'Rate Status:',
          draft: 'Draft',
          normal: 'Normal',
          expired: 'Expired',
          offShelf: 'Offline',
          unknown: 'Unknown'
        },

        // Section titles
        sections: {
          basicInfo: 'Basic Information',
          routeInfo: 'Route Information',
          carrierInfo: 'Carrier Information',
          validitySettings: 'Validity Settings',
          remarks: 'Remarks',
          rateDetails: 'Rate Details'
        },

        // Form fields
        fields: {
          priceNo: 'Rate Number',
          rateType: 'Rate Type',
          por: 'Place of Receipt',
          pol: 'Port of Loading',
          pdl: 'Place of Delivery',
          pod: 'Port of Discharge',
          laneId: 'Route',
          laneCode: 'Route Code',
          shipName: 'Vessel Name',
          voyageNumber: 'Voyage Number',
          etd: 'ETD',
          eta: 'ETA',
          vesselSchedule: 'Vessel Schedule',
          voyage: 'Voyage',
          pot1: 'Transit Port (1st)',
          pot2: 'Transit Port (2nd)',
          pot3: 'Transit Port (3rd)',
          carrier: 'Carrier',
          contractNo: 'Contract No.',
          nac: 'NAC',
          freeContainerDays: 'Free Container Days',
          freeStorageDays: 'Free Storage Days',
          validPeriod: 'Valid Period',
          overweightNote: 'Overweight Notes',
          chargeSpecialNote: 'Special Receiving Instructions',
          notes: 'Notes'
        },

        // Placeholders
        placeholders: {
          selectRateType: 'Select rate type',
          selectPlaceOfDelivery: 'Select place of delivery',
          selectPol: 'Select port of loading',
          selectPortOfDischarge: 'Select port of discharge',
          selectPod: 'Select port of discharge',
          selectLaneId: 'Select route',
          inputLaneCode: 'Enter route code',
          inputShipName: 'Enter vessel name',
          inputVoyageNumber: 'Enter voyage number',
          selectEtd: 'Select estimated departure date',
          selectEta: 'Select estimated arrival date',
          selectVesselSchedule: 'Select vessel schedule',
          copyPriceNo: 'Will be regenerated after copying, format: FCL2506250001',
          autoGeneratePriceNo: 'Auto-generated by backend, format: FCL2506250001',
          selectPot1: 'Select transit port (1st)',
          selectPot2: 'Select transit port (2nd)',
          selectPot3: 'Select transit port (3rd)',
          selectCarrier: 'Select carrier',
          selectContractNo: 'Select contract number',
          selectNac: 'Select NAC',
          inputFreeContainerDays: 'Enter free container days',
          inputFreeStorageDays: 'Enter free storage days',
          startDate: 'Start date',
          endDate: 'End date',
          inputOverweightNote: 'Enter overweight notes',
          inputChargeSpecialNote: 'Enter special receiving instructions',
          inputNotes: 'Enter notes',
          selectFeeName: 'Select fee name',
          autoFill: 'Auto-fill',
          selectBillingUnit: 'Select billing unit',
          inputPrice: 'Enter price',
          inputUnitPrice: 'Enter unit price',
          inputSpecialNote: 'Enter special notes',
          inputVoyage: 'Enter voyage (days)',
          inputContainerPrice: 'Enter price'
        },

        // Form validation messages
        validation: {
          selectPot1: 'Please select transit port (1st)',
          selectCarrier: 'Please select carrier',
          selectValidPeriod: 'Please select valid period'
        },

        // Messages
        messages: {
          selectRateType: 'Please select rate type',
          selectPol: 'Please select port of loading',
          selectPod: 'Please select port of discharge',
          selectLaneId: 'Please select route',
          saveDataFirst: 'Please save data first',
          onlyDraftCanSave: 'Only draft status rates can be saved',
          onlyDraftCanSaveDraft: 'Only draft status rates can save as draft',
          draftSaveSuccess: 'Draft saved successfully',
          draftSaveFailed: 'Failed to save draft',
          rateUpdateSuccess: 'Rate updated successfully!',
          rateCreateSuccess: 'Rate created successfully!',
          rateUpdateAndOnShelfSuccess: 'Rate updated and put online successfully!',
          rateCreateAndOnShelfSuccess: 'Rate created and put online successfully!',
          copySuccess: 'Rate information copied to new page, rate number will be regenerated, please confirm information before saving',
          saveFailed: 'Save failed',
          checkRequiredFields: 'Please check required fields',
          unknownError: 'Unknown error',
          loadDataFailed: 'Failed to load data',
          copyDataLoadFailed: 'Failed to load copy data',
          unknownFee: 'Unknown Fee'
        },

        // Modals
        modals: {
          containerSettings: 'Container Settings',
          aiRecognition: 'AI Smart Recognition'
        },

        // Rate details
        rateDetails: {
          containerBilling: 'Container-based Billing',
          nonContainerBilling: 'Non-container Billing',
          boxTypeSettings: 'Container Type Settings',
          addItem: 'Add Item',
          basicFee: 'Basic Fee',
          delete: 'Delete',
          noData: 'No data',

          // Table column titles
          tableColumns: {
            feeName: 'Fee Name',
            currency: 'Currency',
            billingUnit: 'Billing Unit',
            unitPrice: 'Unit Price',
            specialNote: 'Special Notes',
            actions: 'Actions'
          },
          containerBillingTitle: 'Container-based Billing',
          feeNameTitle: 'Fee Name',
          specialNoteTitle: 'Special Notes',
          currencyTitle: 'Currency',
          unitTitle: 'Billing Unit',
          priceTitle: 'Unit Price'
        },

        // Modal content details
        modalContent: {
          boxTypeSettings: {
            description: 'Select container types to display in the rate table:'
          },
          aiRecognition: {
            uploadTitle: 'Upload Rate File',
            uploadDesc: 'Click or drag files to this area to upload',
            supportedFormats: 'Supported formats: PDF, Excel, Word, images, etc.',
            selectFile: 'Select File',
            aiDescription: 'AI Recognition Description',
            aiFeatures: [
              'AI will automatically recognize port, carrier, price and other information in rate tables',
              'Supports multiple file formats and complex table structures',
              'After recognition is complete, it will create background tasks for processing',
              'Please ensure uploaded files are clear and readable'
            ]
          }
        },

        // Status labels
        statusLabels: {
          rateStatus: 'Rate Status:'
        },

        // Button texts
        buttonTexts: {
          back: 'Back'
        },

        // Enum labels
        enumLabels: {
          // Rate types
          rateTypes: {
            contract: 'Contract Rate',
            spot: 'SPOT E-commerce'
          },

          // Cargo types
          cargoTypes: {
            general: 'General Cargo',
            dangerous: 'Dangerous Goods',
            frozen: 'Frozen Goods',
            special: 'Special Container',
            steelCoil: 'Steel Coil',
            liquid: 'Liquid',
            chemical: 'Chemical',
            textile: 'Textile'
          },

          // Space status
          spaceStatus: {
            smooth: 'Smooth',
            normal: 'Normal',
            singleApply: 'Single Apply',
            overbooked: 'Overbooked',
            notAccepting: 'Not Accepting',
            limited: 'Limited',
            suspended: 'Suspended',
            inquiry: 'Inquiry'
          },

          // Price trends
          priceTrends: {
            rising: 'Price Rising',
            falling: 'Price Falling',
            stable: 'Price Stable'
          },

          // Transit types
          transitTypes: {
            direct: 'Direct',
            transit: 'Transit'
          }
        },

        // Transit ports
        transitPorts: {
          first: 'Transit Port (1st)',
          second: 'Transit Port (2nd)',
          third: 'Transit Port (3rd)',
          selectFirst: 'Please select first transit port',
          selectSecond: 'Please select second transit port',
          selectThird: 'Please select third transit port'
        },

        // Fee names mapping
        feeNames: {
          documentFee: 'Documentation Fee'
        },

        // Notifications
        notifications: {
          aiRecognitionSuccess: 'AI recognition complete, rate information has been automatically filled!',
          fileUploadPending: 'File upload function under development...'
        },

        // Detail page configuration
        detail: {
          // Field labels
          fields: {
            voyage: 'Voyage',
            carrier: 'Carrier',
            contractNo: 'Contract No.',
            nac: 'NAC',
            freeContainerDays: 'Free Container Days',
            freeStorageDays: 'Free Storage Days',
            validPeriod: 'Valid Period',
            overweightRemarks: 'Overweight Remarks',
            receivingInstructions: 'Special Receiving Instructions',
            remarks: 'Remarks'
          },
          // Placeholders
          placeholders: {
            inputVoyage: 'Enter voyage (days)',
            selectCarrier: 'Select carrier',
            selectContractNo: 'Select contract number',
            selectNac: 'Select NAC',
            inputFreeContainerDays: 'Enter free container days',
            inputFreeStorageDays: 'Enter free storage days',
            startDate: 'Start date',
            endDate: 'End date',
            inputOverweightRemarks: 'Enter overweight remarks',
            inputReceivingInstructions: 'Enter special receiving instructions',
            inputRemarks: 'Enter remarks'
          },
          // Messages
          messages: {
            selectCarrier: 'Please select carrier',
            selectValidPeriod: 'Please select valid period',
            selectRateType: 'Please select rate type',
            selectDeparturePort: 'Please select port of loading',
            selectDischargePort: 'Please select port of discharge',
            saveFirst: 'Please save data first',
            loadDataFailed: 'Failed to load data',
            copyDataLoadFailed: 'Failed to load copy data',
            invalidApiResponse: 'Invalid API response format',
            invalidCopyApiResponse: 'Invalid copy data API response format',
            unknownError: 'Unknown error',
            missingFeeId: 'Missing fee ID',
            containerSettingsUpdated: 'Container settings updated'
          },
          // Section titles
          sections: {
            ddSettings: 'D&D'
          },
          // Modals
          modals: {
            selectContainerTypes: 'Select container types to display in the rate table'
          },
          // Page titles
          pageTitles: {
            create: 'Create FCL Rate',
            edit: 'Edit FCL Rate',
            view: 'Rate Details'
          },
          // Rate details
          rateDetails: {
            nonContainerBilling: 'Non-container Billing'
          }
        }
      },

      // Messages
      messages: {
        loadDataFailed: 'Failed to load data',
        addSuccess: 'Added successfully',
        editSuccess: 'Edited successfully',
        deleteSuccess: 'Deleted successfully',
        copySuccess: 'Copied successfully',
        onShelfSuccess: 'Put online successfully',
        offShelfSuccess: 'Put offline successfully',
        batchOnShelfFailed: 'Batch online failed',
        batchOffShelfFailed: 'Batch offline failed',
        batchDeleteFailed: 'Batch delete failed',
        batchPriceFailed: 'Batch price change failed',
        batchTimeFailed: 'Batch time change failed',
        deleteConfirm: 'Are you sure to delete this rate record?',
        exportFilename: 'FCL Container Rate Data',
        unknown: 'Unknown',
        selectTransitType: 'Please select transit type',
        importTitle: 'Import FCL rates',
        importDesc: 'Upload an Excel file (.xls / .xlsx) in FCL3.1 template format. The server validates headers and parses data rows.',
        importSelectFile: 'Choose file',
        importSubmit: 'Import',
        importSelectFileFirst: 'Please select a file first',
        importParsing: 'Uploading and validating…',
        importSuccess:
          'Parsed {sheetCount} sheet(s), {totalRows} row(s). Saved {savedSuccess}, failed {savedFailed}, empty rows skipped {skipped}. Batch: {batchNo}',
        importFailed: 'Import failed',
        templateDownloadFailed: 'Failed to download template'
      }
    },

    // LCL Rate Management
    lcl: {
      title: 'LCL Rate Management',
      description: 'Manage LCL rate information',
      pending: 'LCL rate management function under development...',
      features: 'Will support LCL rate CRUD operations, batch operations and other functions',

      // Placeholder for development
      filters: {},
      placeholders: {},
      columns: {},
      actions: {},
      enums: {},
      messages: {}
    },

    // Air Freight Rate Management
    air: {
      title: 'Air Freight Rate Management',
      description: 'Manage air freight rate information',
      pending: 'Air freight rate management function under development...',
      features: 'Will support air freight rate CRUD operations, batch operations and other functions',

      // Placeholder for development
      filters: {},
      placeholders: {},
      columns: {},
      actions: {},
      enums: {},
      messages: {}
    },

    // Preport Rate Management
    preport: {
      title: 'Preport Rate Management',
      description: 'Manage preport rate information',

      // Page titles
      pageTitles: {
        create: 'Add Preport Rate',
        edit: 'Edit Preport Rate',
        detail: 'Preport Rate Details',
        copy: 'Copy Preport Rate'
      },

      // Filter conditions
      filters: {
        priceNo: 'Rate Number',
        priceNoPlaceholder: 'Please enter rate number',
        priceMethod: 'Rate Type',
        priceMethodPlaceholder: 'Please select rate type',
        pol: 'Port of Loading',
        polPlaceholder: 'Please enter port of loading code',
        wharf: 'Terminal',
        wharfPlaceholder: 'Please enter terminal name',
        status: 'Status',
        statusPlaceholder: 'Please select status',
        beginDate: 'Effective Date',
        beginDatePlaceholder: 'Start date',
        endDate: 'Expiry Date',
        endDatePlaceholder: 'End date'
      },

      // Rate types
      priceMethods: {
        direct: 'Direct',
        branch: 'Branch',
        seaRail: 'Sea-Rail'
      },

      // Branch line types
      branchLines: {
        haiding: 'Haiding Branch',
        zhapu: 'Zhapu Branch'
      },

      // Sea-rail types
      seaRailTypes: {
        huzhou: 'Huzhou Sea-Rail',
        yiwu: 'Yiwu Sea-Rail'
      },

      // Port types
      portTypes: {
        sea: 'Sea Port',
        air: 'Air Port'
      },

      // Status
      status: {
        draft: 'Draft',
        normal: 'Normal',
        expired: 'Expired'
      },

      // Cargo types
      cargoTypes: {
        general: 'General',
        dangerous: 'Dangerous',
        refrigerated: 'Refrigerated',
        special: 'Special',
        steel: 'Steel Coil',
        liquid: 'Liquid',
        chemical: 'Chemical',
        textile: 'Textile'
      },

      // Table columns
      columns: {
        priceNo: 'Rate Number',
        priceMethod: 'Rate Type',
        branchLine: 'Branch Line Type',
        pol: 'Port of Loading',
        wharf: 'Terminal',
        regionItems: 'Origin Region',
        gp20: '20GP',
        gp40: '40GP',
        hc20: '20HC',
        hc40: '40HC',
        hc45: '45HC',
        nor20: '20NOR',
        nor40: '40NOR',
        tk20: '20TK',
        tk40: '40TK',
        ot20: '20OT',
        ot40: '40OT',
        fr20: '20FR',
        fr40: '40FR',
        supplier: 'Supplier',
        validPeriod: 'Valid Period',
        beginDate: 'Effective Date',
        endDate: 'Expiry Date',
        status: 'Status',
        remarks: 'Remarks',
        createdBy: 'Created By',
        modifiedBy: 'Modified By',
        dateCreated: 'Created Date',
        dateModified: 'Modified Date',
        actions: 'Actions'
      },

      // Action buttons
      actions: {
        search: 'Search',
        reset: 'Reset',
        add: 'Add Preport Rate',
        import: 'Import',
        downloadTemplate: 'Download Import Template',
        view: 'Details',
        edit: 'Edit',
        copy: 'Copy',
        more: 'More',
        onShelf: 'Publish',
        offShelf: 'Unpublish',
        delete: 'Delete',
        batchOperations: 'Batch Operations',
        batchOnShelf: 'Batch Publish',
        batchOffShelf: 'Batch Unpublish',
        batchDelete: 'Batch Delete',
        batchPriceChange: 'Batch Price Change',
        aiRecognition: 'AI Recognition',
        batchValidityChange: 'Batch Validity Change'
      },

      // AI Recognition Configuration
      aiRecognition: {
        title: 'Preport Rate AI Recognition',
        uploadTitle: 'Upload Preport Rate Document',
        uploadDesc: 'Supports PDF, Word, Excel formats, AI will automatically recognize rate information',
        aiDescription: 'AI Recognition Description',
        aiFeatures: [
          'AI will automatically recognize port, carrier, price and other information in rate tables',
          'Supports multiple file formats and complex table structures',
          'After recognition is complete, it will create background tasks for processing',
          'Please ensure uploaded files are clear and readable'
        ],
        successTitle: 'AI Recognition Task Submitted',
        successDescription: 'The current task is running in the background, please go to <strong>System Settings - Task Management</strong> to view task progress and results.',
        goToTaskManagement: 'Go to Task Management',
        close: 'Close',
        uploadFile: 'Upload {type} File',
        clickOrDrag: 'Click or drag files to this area to upload',
        supportedFormats: 'Supported formats: PDF, Excel, Word, images, etc.',
        selectFile: 'Select File',
        aiParsing: 'AI Parsing...',
        aiParsingTip: 'AI is parsing the document, please wait...'
      },

      // Import feature
      import: {
        title: 'Preport Rate - Import',
        downloadTemplate: 'Download Preport Rate Import Template',
        breadcrumb: 'Preport Rate - Import',
        reminderTitle: 'Function Note',
        reminderLines: [
          '1. Freight rates will be saved to draft packages and take effect after publishing.',
          '2. If there are corresponding rates in the same period, the original rates will be overwritten.'
        ],
        dragText: 'Click or drag Excel file here',
        parsing: 'Parsing...',
        cancel: 'Cancel',
        submit: 'Submit Parsing'
      },

      // Batch operations
      batch: {
        selected: 'Selected {count} items',
        onShelfConfirm: 'Are you sure to publish the selected {count} rates?',
        offShelfConfirm: 'Are you sure to unpublish the selected {count} rates?',
        deleteConfirm: 'Are you sure to delete the selected {count} rates? This action cannot be undone.',
        onShelfSuccess: 'Successfully published {count} rates',
        offShelfSuccess: 'Successfully unpublished {count} rates',
        deleteSuccess: 'Successfully deleted {count} rates',
        // Batch price change
        priceChange: {
          title: 'Batch Modify Rates',
          selectedRates: 'Selected {count} rates',
          operationReminder: 'Operation Reminder',
          reminderContent: 'This operation will modify the fee amounts of all selected rates. Please proceed with caution. You can choose specific fee types for batch modification. Leaving empty means the container type price remains unchanged. If the original rate does not have a corresponding container type price, the adjustment will not take effect. Please first choose whether to increase or decrease the price, and always enter positive integers in the input boxes.',
          selectCharge: 'Select Fee',
          selectChargeDesc: 'Select the fee type to batch modify prices, such as preport fee, handling fee, etc.',
          selectChargePlaceholder: 'Please select the fee type to modify',
          selectPriceMode: 'Select Price Mode',
          priceIncrease: 'Price Increase',
          priceDecrease: 'Price Decrease',
          setPriceMode: 'Fixed Amount',
          priceIncreaseDesc: 'Select price increase mode, enter the increase amount (positive integer) in the input box',
          priceDecreaseDesc: 'Select price decrease mode, enter the decrease amount (positive integer) in the input box',
          setPriceDesc: 'Select fixed amount mode, enter the price of any container type (positive integer)',
          setPrice: 'Set Price',
          selectChargeFirst: 'Please select fee type first, then set price',
          rateListToModify: 'Rates to be modified',
          rateDetailsList: 'Rates to be modified list',
          rateNo: 'Rate No.',
          origin: 'Origin',
          pol: 'Port of Loading',
          supplier: 'Supplier',
          status: 'Status',
          showChargePrice: '(Show {charge} price)',
          showAllCharges: '(Show total price of all container-based charges)',
          noData: 'No data',
          selectedSummary: 'Selected {count} rates, will batch modify {type} prices.',
          currentChargeType: 'Current selected charge type: ',
          confirmOperation: 'This operation will modify the ',
          confirmOperationSuffix: ' amount of selected rates, please confirm data accuracy',
          selectChargeAndPrice: 'Please select fee type and set price first, then confirm modification',
          confirm: 'Confirm',
          cancel: 'Cancel'
        },
        // Batch time change
        timeChange: {
          title: 'Batch Time Change',
          operationAlert: 'This operation will batch modify the time information of selected rates, please operate with caution',
          etdTab: 'ETD (Estimated Time of Departure)',
          etaTab: 'ETA (Estimated Time of Arrival)',
          validDateTab: 'Validity Period',
          setNewEtd: 'Set New ETD (Estimated Time of Departure)',
          setNewEta: 'Set New ETA (Estimated Time of Arrival)',
          setNewValidDate: 'Set New Validity Period',
          newEtd: 'New ETD:',
          newEta: 'New ETA:',
          validPeriod: 'Validity Period:',
          selectEtd: 'Select estimated departure date',
          selectEta: 'Select estimated arrival date',
          startDate: 'Start Date',
          endDate: 'End Date',
          currentEtd: 'Current ETD',
          currentEta: 'Current ETA',
          currentValidPeriod: 'Current Validity Period',
          rateListToModify: 'Rates to be modified',
          selectedSummary: 'Selected {count} rates, will batch modify {type} information',
          cancel: 'Cancel',
          confirm: 'Confirm',
          etd: 'ETD',
          eta: 'ETA',
          validDate: 'Validity Period',
          // New fields
          operationReminder: 'Operation Reminder',
          reminderContent: 'This operation will modify the time information of all selected rates, please operate with caution.',
          setNewEtdTitle: 'Set New ETD',
          setNewEtaTitle: 'Set New ETA',
          setNewValidDateTitle: 'Set New Validity Period',
          etdLabel: 'ETD',
          etaLabel: 'ETA',
          validDateLabel: 'Validity Period',
          selectEtdPlaceholder: 'Please select ETD',
          selectEtaPlaceholder: 'Please select ETA',
          selectValidDateRange: 'Please select validity period time range',
          invalidDateRange: 'Start date must be earlier than end date',
          startDatePlaceholder: 'Start Date',
          endDatePlaceholder: 'End Date',
          selectedRatesList: 'Rates to be modified',
          // New success messages
          etdUpdateSuccess: 'Successfully modified ETD for {count} rates',
          etaUpdateSuccess: 'Successfully modified ETA for {count} rates',
          validDateUpdateSuccess: 'Successfully modified validity period for {count} rates',
          updateFailed: 'Batch time modification failed'
        },
        // Validity period modification
        validityChange: {
          title: 'Batch Modify Validity Period',
          validityPeriod: 'Validity Period',
          selectValidityPeriod: 'Please select validity period',
          startDate: 'Start Date',
          endDate: 'End Date'
        },
        // Batch operation confirmation
        confirm: {
          online: 'Confirm Batch Publish',
          offline: 'Confirm Batch Unpublish',
          delete: 'Confirm Batch Delete',
          onlineContent: 'Are you sure to publish the selected {count} rates?',
          offlineContent: 'Are you sure to unpublish the selected {count} rates?',
          deleteContent: 'Are you sure to delete the selected {count} rates? This action cannot be undone.'
        },
        // Batch operation messages
        messages: {
          onlineSuccess: 'Batch publish successful',
          onlineFailed: 'Batch publish failed',
          offlineSuccess: 'Batch unpublish successful',
          offlineFailed: 'Batch unpublish failed',
          deleteSuccess: 'Batch delete successful',
          deleteFailed: 'Batch delete failed',
          validityChangeSuccess: 'Batch validity period modification successful',
          validityChangeFailed: 'Batch validity period modification failed',
          selectRatesFirst: 'Please select rates to {action} first',
          selectRatesToOnline: 'Please select rates to publish first',
          selectRatesToOffline: 'Please select rates to unpublish first',
          selectRatesToModify: 'Please select rates to modify first',
          selectRatesToDelete: 'Please select rates to delete first',
          onlyDraftCanOnline: 'Only draft status rates can be published, current selection includes: {status} status rates',
          onlyNormalCanOffline: 'Only normal status rates can be unpublished, current selection includes: {status} status rates',
          onlyDraftCanModify: 'Only draft status rates can be modified, please reselect',
          onlyDraftCanDelete: 'Only draft status rates can be deleted, current selection includes: {status} status rates'
        }
      },

      // Fee types
      chargeItems: {
        preportFee: 'Preport Fee',
        handlingFee: 'Handling Fee',
        documentFee: 'Document Fee',
        truckingFee: 'Trucking Fee'
      },

      // Batch price change
      batchPrice: {
        title: 'Batch Price Change',
        chargeItemRequired: 'Please select the fee type to modify',
        priceInputRequired: 'Please enter price for at least one container type',
        priceUpdateFailed: 'Batch price modification failed',
        setPriceSuccess: 'Successfully set fixed price for {count} rates',
        increaseSuccess: 'Successfully increased price for {count} rates',
        decreaseSuccess: 'Successfully decreased price for {count} rates',
        pricePlaceholders: {
          increase: 'Increase amount',
          decrease: 'Decrease amount',
          setPrice: 'Fixed price',
          default: 'Enter price'
        }
      },

      // Messages
      messages: {
        loadDataFailed: 'Failed to load data',
        loadDataError: 'Failed to load preport rate data:',
        copySuccess: 'Rate information has been copied to the new page, rate number will be regenerated, please confirm information before saving',
        batchOnShelfFailed: 'Batch publish failed',
        batchOffShelfFailed: 'Batch unpublish failed',
        batchDeleteFailed: 'Batch delete failed',
        importPending: 'Import feature under development',
        templateDownloadFailed: 'Template download failed',
        onShelfSuccess: 'Rate published',
        offShelfSuccess: 'Rate unpublished',
        statusToggleFailed: 'Status toggle failed',
        deleteConfirm: 'Are you sure to delete this rate record?',
        deleteSuccess: 'Delete successful',
        deleteFailed: 'Delete failed',
        totalCount: 'Total {count}',
        unknown: 'Unknown',
        exportFilename: 'Preport Rate Data'
      },

      // Detail page
      detail: {
        // Action buttons
        pageActions: {
          back: 'Back',
          saveDraft: 'Save Draft',
          saveAndPublish: 'Publish Directly',
          save: 'Save',
          edit: 'Edit',
          copy: 'Copy',
          copyAlert: 'Copying rate information, rate number will be automatically regenerated, please confirm all information before saving'
        },
        // Basic information
        basicInfo: {
          title: 'Basic Information',
          priceNo: 'Rate Number',
          priceNoPlaceholder: 'Auto generated by backend, format: PREYYMMDD0001',
          priceNoCopyPlaceholder: 'Will be regenerated after copy, format: PREYYMMDD0001',
          priceMethod: 'Rate Type',
          priceMethodRequired: 'Please select rate type',
          branchLine: 'Branch Line Type',
          branchLinePlaceholder: 'Please select branch line type',
          branchLineRequired: 'Please select branch line type',
          seaRailType: 'Sea-Rail Type',
          seaRailTypePlaceholder: 'Please select sea-rail type',
          seaRailTypeRequired: 'Please select sea-rail type'
        },
        // Region selection
        regionSelection: {
          title: 'Origin Region Selection',
          aiRecognition: 'AI Recognition',
          addRegion: 'Add Region',
          region: 'Region {index}',
          atLeastOneRegion: 'At least one region is required',
          administrativeDivision: 'Administrative Division',
          detailedLocation: 'Detailed Location',
          detailedAddress: 'Detailed Address',
          selectProvince: 'Please select province',
          selectCity: 'Please select city',
          selectDistrict: 'Please select district/county',
          selectStreet: 'Please select street',
          selectCommunity: 'Please select community',
          enterDetailedAddress: 'Please enter detailed address (house number, building, room, etc.)',
          noRegionSet: 'No region set',
          clickToExpand: 'Click to expand region information',
          selectRegionInfo: 'Please select region information'
        },
        // Add region i18n
        region: {
          china: 'China'
        },
        // Port information
        portInfo: {
          title: 'Port Information',
          pol: 'Port of Loading',
          polRequired: 'Please select port of loading',
          polPlaceholder: 'Please select port of loading',
          wharf: 'Terminal',
          wharfPlaceholder: 'Please enter terminal',
          supplier: 'Supplier',
          supplierRequired: 'Please enter supplier',
          supplierPlaceholder: 'Please enter supplier',
          validPeriod: 'Valid Period',
          validPeriodRequired: 'Please select valid period',
          validPeriodStart: 'Start Date',
          validPeriodEnd: 'End Date',
          etdPlaceholder: 'Please select estimated departure date',
          etaPlaceholder: 'Please select estimated arrival date',
          remarks: 'Remarks',
          remarksPlaceholder: 'Please enter remarks'
        },
        // Container type settings
        boxTypeSettings: {
          title: 'Container Type Settings',
          selectBoxTypes: 'Select container type columns to display:',
          updateSuccess: 'Container type settings updated'
        },
        // Rate details
        rateDetails: {
          title: 'Rate Details',
          containerRateItems: 'Container-based Rate Items',
          nonContainerRateItems: 'Non-container Rate Items',
          addRateItem: 'Add Rate Item',
          rateItem: 'Rate Item {index}',
          atLeastOneRateItem: 'At least one rate item is required',
          addChargingItem: 'Add Charging Item',
          chargingItem: 'Charging Item {index}',
          atLeastOneChargingItem: 'At least one charging item is required',
          specialNote: 'Special Notes',
          specialNotePlaceholder: 'Special notes',
          feeName: 'Fee Name',
          feeNamePlaceholder: 'Please select fee name',
          basicFeePlaceholder: 'Please select basic fee',
          currency: 'Currency',
          unit: 'Billing Unit',
          unitPlaceholder: 'Please select billing unit',
          price: 'Unit Price',
          pricePlaceholder: 'Please enter unit price',
          priceInputPlaceholder: 'Please enter price',
          autoFill: 'Auto Fill',
          basicFee: 'Basic Fee',
          piece: 'Piece',
          container: 'Container',
          item: 'Item',
          ton: 'Ton',
          cubicMeter: 'Cubic Meter',
          teu: 'TEU',
          actions: 'Actions',
          // 新增缺失的国际化字段
          containerBillingTitle: 'Container-based Billing Items',
          feeNameTitle: 'Fee Name',
          specialNoteTitle: 'Special Notes'
        },
        // Save messages
        saveMessages: {
          updateSuccess: 'Rate updated and published successfully!',
          createSuccess: 'Rate created and published successfully!',
          saveFailed: 'Save failed: ',
          unknownError: 'Unknown error',
          checkRequired: 'Please check required fields',
          draftSaveSuccess: 'Draft saved successfully',
          draftSaveFailed: 'Save draft failed: ',
          onlyDraftCanSave: 'Only draft status rates can be saved',
          onlyDraftCanSaveDraft: 'Only draft status rates can be saved as draft'
        },
        // Data loading
        dataLoading: {
          loadFailed: 'Failed to load data: ',
          copyLoadFailed: 'Failed to load copy data: '
        },
        // Messages
        messages: {
          loadFeeDataFailed: 'Failed to load fee data',
          noBasicFeeFound: 'No applicable basic fee found, please select manually',
          loadBasicFeeFailed: 'Failed to load basic fee'
        }
      },

      // Delivery address types
      deliveryAddressTypes: {
        thirdParty: 'Third Party Warehouse',
        customer: 'Customer Address',
        agent: 'Agent Address'
      }
    },

    // Last Mile Rate Management
    lastmile: {
      title: 'Last Mile Rate Management',
      description: 'Manage last mile rate information',

      // Page titles
      pageTitles: {
        create: 'Add Last Mile Rate',
        edit: 'Edit Last Mile Rate',
        detail: 'Last Mile Rate Details',
        copy: 'Copy Last Mile Rate'
      },

      // Filter fields (based on existing lastmileFilterFields)
      filters: {
        priceMethod: 'Rate Type',
        status: 'Rate Status',
        destinationPort: 'Destination Port',
        agent: 'Agent Name',
        currency: 'Currency',
        feeName: 'Fee Name',
        unit: 'Unit',
        addressType: 'Address Type',
        country: 'Country',
        postalCode: 'Postal Code',
        state: 'State',
        city: 'City',
        district: 'District',
        warehouseCode: 'Warehouse Code'
      },

      // Placeholders
      placeholders: {
        priceMethod: 'Please select rate type',
        status: 'Please select rate status',
        destinationPort: 'Please select destination port',
        agent: 'Please enter agent name',
        currency: 'Please select currency',
        feeName: 'Please select fee name',
        unit: 'Please select unit',
        addressType: 'Please select address type',
        country: 'Please select country',
        postalCode: 'Please select postal code',
        state: 'Please select state',
        city: 'Please select city',
        district: 'Please select district',
        warehouseCode: 'Please enter warehouse code'
      },

      // Rate types
      priceMethods: {
        direct: 'Direct',
        branch: 'Branch',
        seaRail: 'Sea-Rail'
      },

      // Status
      status: {
        draft: 'Draft',
        normal: 'Normal',
        expired: 'Expired',
        offline: 'Offline'
      },

      // Table columns
      columns: {
        priceNo: 'Last Mile Rate Number',
        pods: 'Destination Port',
        deliveryAddressType: 'Address Type',
        postalCode: 'Postal Code',
        address: 'Address',
        warehouseCode: 'Warehouse Code',
        agentName: 'Agent Name',
        gp20: '20GP',
        gp40: '40GP',
        hc20: '20HC',
        hc40: '40HC',
        hc45: '45HC',
        nor20: '20NOR',
        nor40: '40NOR',
        tk20: '20TK',
        tk40: '40TK',
        ot20: '20OT',
        ot40: '40OT',
        fr20: '20FR',
        fr40: '40FR',
        validPeriod: 'Valid Period',
        status: 'Status',
        createdBy: 'Created By',
        dateCreated: 'Created Date',
        modifiedBy: 'Modified By',
        modifyDate: 'Modified Date',
        remarks: 'Remarks',
        actions: 'Actions'
      },

      // Action buttons
      actions: {
        search: 'Search',
        reset: 'Reset',
        import: 'Import',
        add: 'Add Last Mile Rate',
        view: 'Details',
        edit: 'Edit',
        copy: 'Copy',
        more: 'More',
        onShelf: 'Publish',
        offShelf: 'Unpublish',
        delete: 'Delete',
        batchOperations: 'Batch Operations',
        batchOnShelf: 'Batch Publish',
        batchOffShelf: 'Batch Unpublish',
        batchDelete: 'Batch Delete',
        batchPriceChange: 'Batch Price Change',
        aiRecognition: 'AI Recognition',
        batchValidityChange: 'Batch Validity Change'
      },

      // AI recognition
      aiRecognition: {
        title: 'Last Mile Rate AI Recognition',
        uploadTitle: 'Upload Last Mile Rate Document',
        uploadDesc: 'Supports PDF, Word, Excel formats. AI will automatically recognize rate information',
        priceTypeName: 'Last Mile Rate'
      },

      // Batch operations
      batch: {
        selected: 'Selected {count} items',
        onShelfConfirm: 'Are you sure you want to publish the selected {count} rates?',
        offShelfConfirm: 'Are you sure you want to unpublish the selected {count} rates?',
        deleteConfirm: 'Are you sure you want to delete the selected {count} rates? This action cannot be undone.',
        onShelfSuccess: 'Successfully published {count} rates',
        offShelfSuccess: 'Successfully unpublished {count} rates',
        deleteSuccess: 'Successfully deleted {count} rates',

        // Batch price change
        priceChange: {
          title: 'Batch Price Change',
          selectedRates: 'Selected {count} freight rates',
          operationReminder: 'Operation Reminder',
          reminderContent: 'This operation will modify the fee amount of all selected freight rates, please operate with caution. You can choose specific fee types for batch modification. Leaving it blank means the price for that container type will not change. If the original freight rate does not have a corresponding container type price, the adjustment will not take effect. Please first choose whether to increase or decrease the price, and always enter positive integers in the input box.',
          selectCharge: 'Select Fee',
          selectChargeDesc: 'Select the fee type to modify prices in batch, such as last mile fees, delivery fees, etc.',
          selectChargePlaceholder: 'Please select the fee type to modify',
          selectPriceMode: 'Select Price Change Mode',
          priceIncrease: 'Price Increase',
          priceDecrease: 'Price Decrease',
          setPriceMode: 'Fixed Amount',
          priceIncreaseDesc: 'Select price increase mode, enter the increase amount in the input box (positive integer)',
          priceDecreaseDesc: 'Select price decrease mode, enter the decrease amount in the input box (positive integer)',
          setPriceDesc: 'Select fixed amount mode, enter the price for any container type (positive integer)',
          setPrice: 'Set Price',
          selectChargeFirst: 'Please select fee type first, then set price',
          rateListToModify: 'Rates to be modified',
          rateDetailsList: 'Rates to be modified list',
          rateNo: 'Last Mile Rate Number',
          destinationPort: 'Destination Port',
          address: 'Address',
          showChargePrice: '(Show {charge} price)',
          showAllCharges: '(Show all fees total)',
          noData: 'No data',
          selectedSummary: 'A total of {count} freight rates have been selected, and the prices for {type} will be modified in batch.',
          currentChargeType: 'Current selected fee type:',
          confirmOperation: 'This operation will modify the selected freight rates',
          confirmOperationSuffix: 'amount, please confirm data accuracy',
          selectChargeAndPrice: 'Please first select fee type and set price, then confirm modification',
          confirm: 'Confirm',
          cancel: 'Cancel',
          priceInputPlaceholder: 'Please enter price',
          priceInputRequired: 'Please enter price for at least one container type',
          priceUpdateFailed: 'Batch price modification failed',
          setPriceSuccess: 'Successfully set fixed price for {count} rates',
          increaseSuccess: 'Successfully increased price for {count} rates',
          decreaseSuccess: 'Successfully decreased price for {count} rates',
          chargeItemRequired: 'Please select fee type first',
          batchPriceSuccess: 'Batch price modification successful',
          batchPriceFailed: 'Batch price modification failed',
          batchPriceError: 'Batch price modification error',
          pricePlaceholders: {
            increase: 'Enter increase amount',
            decrease: 'Enter decrease amount',
            setPrice: 'Enter price',
            default: 'Enter price'
          }
        },

        // Batch validity change
        validityChange: {
          title: 'Batch Validity Change',
          selectValidityMode: 'Select validity change mode',
          extendMode: 'Extend Validity',
          shortenMode: 'Shorten Validity',
          setMode: 'Set Fixed Validity',
          extendDays: 'Extend Days',
          shortenDays: 'Shorten Days',
          startDate: 'Start Date',
          endDate: 'End Date',
          selectValidityModeFirst: 'Please select validity change mode first',
          validityUpdateFailed: 'Batch validity modification failed',
          extendSuccess: 'Successfully extended validity for {count} rates',
          shortenSuccess: 'Successfully shortened validity for {count} rates',
          setValiditySuccess: 'Successfully set validity for {count} rates'
        },

        // Batch time change
        timeChange: {
          title: 'Batch Time Change',
          operationAlert: 'This operation will batch modify the time information of selected rates, please operate with caution',
          etdTab: 'ETD (Estimated Time of Departure)',
          etaTab: 'ETA (Estimated Time of Arrival)',
          validDateTab: 'Validity Period',
          setNewEtd: 'Set New ETD (Estimated Time of Departure)',
          setNewEta: 'Set New ETA (Estimated Time of Arrival)',
          setNewValidDate: 'Set New Validity Period',
          newEtd: 'New ETD:',
          newEta: 'New ETA:',
          validPeriod: 'Validity Period:',
          selectEtd: 'Select estimated departure date',
          selectEta: 'Select estimated arrival date',
          startDate: 'Start Date',
          endDate: 'End Date',
          currentEtd: 'Current ETD',
          currentEta: 'Current ETA',
          currentValidPeriod: 'Current Validity Period',
          rateListToModify: 'Rates to be modified',
          selectedSummary: 'Selected {count} rates, will batch modify {type} information',
          cancel: 'Cancel',
          confirm: 'Confirm',
          etd: 'ETD',
          eta: 'ETA',
          validDate: 'Validity Period',
          // New fields
          operationReminder: 'Operation Reminder',
          reminderContent: 'This operation will modify the time information of all selected rates, please operate with caution.',
          setNewEtdTitle: 'Set New ETD',
          setNewEtaTitle: 'Set New ETA',
          setNewValidDateTitle: 'Set New Validity Period',
          etdLabel: 'ETD',
          etaLabel: 'ETA',
          validDateLabel: 'Validity Period',
          selectEtdPlaceholder: 'Please select ETD',
          selectEtaPlaceholder: 'Please select ETA',
          startDatePlaceholder: 'Start Date',
          endDatePlaceholder: 'End Date',
          selectedRatesList: 'Rates to be modified',
          // New success messages
          etdUpdateSuccess: 'Successfully modified ETD for {count} rates',
          etaUpdateSuccess: 'Successfully modified ETA for {count} rates',
          validDateUpdateSuccess: 'Successfully modified validity period for {count} rates',
          updateFailed: 'Batch time modification failed'
        }
      },

      // Messages
      messages: {
        loadDataFailed: 'Failed to load data',
        loadDataError: 'Failed to load last mile rate data:',
        copySuccess: 'Rate information has been copied to the new page. Rate number will be regenerated. Please confirm information before saving',
        batchOnShelfFailed: 'Batch publish failed',
        batchOffShelfFailed: 'Batch unpublish failed',
        batchDeleteFailed: 'Batch delete failed',
        importPending: 'Import feature is under development',
        onShelfSuccess: 'Rate has been published',
        offShelfSuccess: 'Rate has been unpublished',
        statusToggleFailed: 'Status toggle failed',
        deleteConfirm: 'Are you sure you want to delete this rate record?',
        deleteSuccess: 'Delete successful',
        deleteFailed: 'Delete failed',
        totalCount: 'Total {count} items',
        unknown: 'Unknown',
        exportFilename: 'Last Mile Rate Data'
      },

      // Detail page
      detail: {
        // Section titles
        sections: {
          basicInfo: 'Basic Information',
          destinationInfo: 'Destination Port Information',
          rateInfo: 'Rate Information'
        },

        // Container type settings
        boxTypeSettings: {
          title: 'Container Type Settings',
          selectBoxTypes: 'Select container type columns to display:',
          updateSuccess: 'Container type settings updated'
        },

        // Page action buttons
        pageActions: {
          back: 'Back',
          saveDraft: 'Save Draft',
          saveAndPublish: 'Publish Directly',
          edit: 'Edit',
          copy: 'Copy',
          copyAlert: 'Copying rate information. Rate number will be automatically regenerated. Please confirm all information before saving'
        },

        // Form buttons
        buttons: {
          back: 'Back',
          saveDraft: 'Save Draft',
          directOnline: 'Direct Online',
          save: 'Save',
          edit: 'Edit',
          copy: 'Copy'
        },

        // Basic information
        basicInfo: {
          title: 'Basic Information',
          priceNo: 'Last Mile Rate Number',
          priceNoPlaceholder: 'Auto-generated by backend, format: LMRYYMMDD0001',
          priceNoCopyPlaceholder: 'Will be regenerated after copying, format: LMRYYMMDD0001',

          // New fields
          priceMethod: 'Rate Type',
          priceMethodRequired: 'Please select rate type',
          priceMethodPlaceholder: 'Please select rate type',
          destinationPort: 'Destination Port',
          destinationPortRequired: 'Please select destination port',
          destinationPortPlaceholder: 'Please select destination port',
          agent: 'Agent Name',
          agentRequired: 'Please enter agent name',
          agentPlaceholder: 'Please enter agent name',
          validPeriod: 'Validity Period',
          validPeriodRequired: 'Please select validity period',
          validPeriodStart: 'Start Date',
          validPeriodEnd: 'End Date'
        },

        // Delivery address selection
        regionSelection: {
          title: 'Delivery Address Selection',
          aiRecognition: 'AI Recognition',
          addRegion: 'Add Delivery Address',
          region: 'Delivery Address {index}',
          atLeastOneRegion: 'At least one delivery address must be retained',
          administrativeDivision: 'Administrative Division',
          detailedLocation: 'Detailed Location',
          detailedAddress: 'Detailed Address',
          selectProvince: 'Please select province',
          selectCity: 'Please select city',
          selectDistrict: 'Please select district',
          selectStreet: 'Please select street',
          selectCommunity: 'Please select community',
          enterDetailedAddress: 'Please enter detailed address (house number, building number, room number, etc.)',
          noRegionSet: 'No region set',
          clickToExpand: 'Click to expand and set region information',

          // New fields
          country: 'Country',
          state: 'State/Province',
          city: 'City',
          district: 'District',
          street: 'Street',
          community: 'Community',

          // Additional fields for last-mile rate management
          deliveryAddress: 'Delivery Address',
          addressType: 'Address Type',
          thirdPartyAddress: 'Third Party Address',
          amazonWarehouse: 'Amazon Warehouse',
          easyWarehouse: 'Ecang',
          notSelected: 'Not Selected',
          postalCode: 'Postal Code',
          warehouseCode: 'Warehouse Code',
          easyWarehouseCode: 'Ecang Code',
          destinationPort: 'Destination Port',
          selectDestinationPort: 'Please select destination port',
          multiplePortsTip: 'Tip: Multiple destination ports can be selected, indicating that rates from these destination ports to the same delivery address are the same',
          agent: 'Agent Name',
          enterAgentName: 'Please enter agent name',
          etd: 'ETD',
          selectEtd: 'Please select ETD',
          eta: 'ETA',
          selectEta: 'Please select ETA',
          validPeriod: 'Validity Period',
          startDate: 'Start Date',
          endDate: 'End Date',
          remarks: 'Remarks',
          enterRemarks: 'Please enter remarks',
          selectAmazonWarehouse: 'Please select Amazon warehouse',
          selectEasyWarehouse: 'Please select Ecang warehouse'
        },

        // Rate details
        rateDetails: {
          title: 'Rate Details',
          containerRate: 'Container-based Pricing',
          nonContainerRate: 'Non-container-based Pricing',
          addContainerRate: 'Add Container-based Pricing',
          addNonContainerRate: 'Add Non-container-based Pricing',
          atLeastOneRateItem: 'At least one rate item must be retained',

          // New fields
          feeName: 'Fee Name',
          feeNamePlaceholder: 'Please select fee name',
          currency: 'Currency',
          currencyPlaceholder: 'Please select currency',
          unit: 'Billing Unit',
          unitPlaceholder: 'Please select billing unit',
          price: 'Unit Price',
          pricePlaceholder: 'Please enter unit price',
          specialNote: 'Special Remarks',
          specialNotePlaceholder: 'Please enter special remarks',
          actions: 'Actions'
        },

        // Form validation
        validation: {
          podsRequired: 'Please select at least one destination port',
          agentRequired: 'Please enter agent name',
          validPeriodRequired: 'Please select validity period',
          regionRequired: 'At least one delivery address must be retained',
          rateItemRequired: 'At least one rate item must be retained',

          // New validation
          priceMethodRequired: 'Please select rate type',
          destinationPortRequired: 'Please select destination port',
          feeNameRequired: 'Please select fee name',
          currencyRequired: 'Please select currency',
          unitRequired: 'Please select billing unit',
          priceRequired: 'Please enter unit price',
          pricePositive: 'Unit price must be greater than 0'
        },

        // Save messages
        saveMessages: {
          updateSuccess: 'Rate updated and published successfully!',
          createSuccess: 'Rate created and published successfully!',
          saveFailed: 'Save failed: ',
          unknownError: 'Unknown error',
          checkRequired: 'Please check required fields',
          draftSaveSuccess: 'Draft saved successfully',
          draftSaveFailed: 'Save draft failed: ',
          onlyDraftCanSave: 'Only draft status rates can be saved',
          onlyDraftCanSaveDraft: 'Only draft status rates can be saved as draft',
          onlyDraftCanEdit: 'Only draft status rates can be edited'
        },

        // Data loading
        dataLoading: {
          loadFailed: 'Failed to load data: ',
          copyLoadFailed: 'Failed to load copy data: ',
          loadFeeDataFailed: 'Failed to load fee data',
          noBasicFeeFound: 'No applicable basic fee found, please select manually',
          loadBasicFeeFailed: 'Failed to load basic fee'
        },

        // Page titles
        pageTitles: {
          copyLastmileRate: 'Copy Last Mile Rate',
          addLastmileRate: 'Add Last Mile Rate',
          editLastmileRate: 'Edit Last Mile Rate',
          lastmileRateDetail: 'Last Mile Rate Details',
          copyRate: 'Copy Last Mile Rate',
          createRate: 'Create Last Mile Rate',
          editRate: 'Edit Last Mile Rate',
          rateDetail: 'Last Mile Rate Details'
        },

        // Export columns
        exportColumns: {
          priceNo: 'Last Mile Rate Number',
          pods: 'Destination Port',
          deliveryAddressType: 'Delivery Address Type',
          postalCode: 'Postal Code',
          address: 'Address',
          warehouseCode: 'Warehouse Code',
          agentName: 'Agent Name',
          gp20Total: '20GP',
          gp40Total: '40GP',
          hc40Total: '40HC',
          nor40Total: '40NOR',
          hc45Total: '45HC',
          beginDate: 'Validity Start',
          endDate: 'Validity End',
          status: 'Status',
          createdBy: 'Created By',
          createdTime: 'Created Date',
          modifiedBy: 'Modified By',
          updatedTime: 'Modified Date',
          remarks: 'Remarks'
        },


        // Form fields
        formFields: {
          priceNo: 'Last Mile Rate Number',
          deliveryAddressSelection: 'Delivery Address Selection',
          addAddress: 'Add Address',
          deliveryAddress: 'Delivery Address {index}',
          addressType: 'Address Type',
          thirdPartyAddress: 'Third Party Address',
          amazonWarehouse: 'Amazon Warehouse',
          easyWarehouse: 'Ecang',
          warehouseCode: 'Warehouse Code',
          easyWarehouseCode: 'Ecang Code',
          destinationPort: 'Destination Port',
          agentName: 'Agent Name',
          remarks: 'Remarks'
        },

        // Rate info
        rateInfo: {
          containerRateProjects: 'Container-based Billing Projects',
          boxTypeSettings: 'Container Type Settings',
          addProject: 'Add Project',
          nonContainerRateProjects: 'Non-container Billing Projects',
          feeName: 'Fee Name',
          currency: 'Currency',
          billingUnit: 'Billing Unit',
          unitPrice: 'Unit Price',
          specialNote: 'Special Notes',
          actions: 'Actions',
          basicFee: 'Basic Fee',
          delete: 'Delete',

          // Additional fields for last-mile rate management
          selectFeeName: 'Please select fee name',
          autoFill: 'Auto-filled from basic fee',
          enterPrice: 'Please enter price',
          selectBillingUnit: 'Please select billing unit',
          enterUnitPrice: 'Please enter unit price',
          noData: 'No data',

          // Table columns
          columns: {
            feeName: 'Fee Name',
            currency: 'Currency',
            billingUnit: 'Billing Unit',
            unitPrice: 'Unit Price',
            specialNote: 'Special Notes',
            actions: 'Actions'
          }
        },

        // Messages
        messages: {
          copyingRateInfo: 'Copying rate information, rate number will be automatically regenerated, please confirm all information before saving',
          multiplePortsTip: 'Tip: Multiple destination ports can be selected, indicating that rates from these destination ports to the same delivery address are the same',
          selectBoxTypesToShow: 'Select container types to display in the rate table:',
          boxTypeSettingsUpdated: 'Container type settings updated',
          atLeastOneAddress: 'At least one address must be retained',
          draftSavedSuccess: 'Draft saved successfully',
          copiedToNewPage: 'Rate information copied to new page, rate number will be regenerated, please confirm information before saving',
          onlyDraftCanEdit: 'Only draft status rates can be edited',
          dataLoadFailed: 'Failed to load data: {error}',
          unknownError: 'Unknown error',
          feeDataLoadFailed: 'Failed to load fee data',
          basicFeeNotFound: 'Basic fee not found'
        },

        // Container types
        boxTypes: {
          gp20: '20GP',
          gp40: '40GP',
          hc40: '40HC',
          hc45: '45HC',
          nor40: '40NOR',
          nor20: '20NOR',
          hc20: '20HC',
          tk20: '20TK',
          tk40: '40TK',
          ot20: '20OT',
          ot40: '40OT',
          fr20: '20FR',
          fr40: '40FR'
        },

        // Modals
        modals: {
          containerSettings: 'Container Type Settings',
          selectBoxTypes: 'Select container types to display in the rate table:'
        },

        // Batch operations
        batchOperations: {
          title: 'Batch Time Modification',
          etd: 'ETD',
          eta: 'ETA',
          validDate: 'Validity Period',
          operationReminder: 'Operation Reminder',
          operationReminderTitle: 'Operation Reminder',
          operationReminderContent: 'This operation will modify the time information of all selected rates, please operate with caution.',
          setNewEtd: 'Set New ETD',
          setNewEta: 'Set New ETA',
          setNewValidDate: 'Set New Validity Period',
          selectedRatesList: 'Rates to be modified',
          currentEtd: 'Current ETD',
          currentEta: 'Current ETA',
          currentValidDate: 'Current Validity Period',
          summaryInfo: 'Selected {count} rates, will modify their {type} information',
          tipText: 'This operation will modify the time information of selected rates, please confirm data accuracy',
          cancel: 'Cancel',
          confirm: 'Confirm'
        }
      }
    },

    // Increase Rules Management
    increaseRules: {
      title: 'Increase Rules Management',
      description: 'Manage increase rules for various rate types',

      // Page titles
      pageTitles: {
        create: 'Add Increase Rule',
        edit: 'Edit Increase Rule',
        detail: 'Increase Rule Details',
        copy: 'Copy Increase Rule'
      },

      // Rule types
      ruleTypes: {
        fcl: 'FCL Ocean Freight',
        preport: 'Pre-port Rate',
        lastmile: 'Last Mile Rate',
        lcl: 'LCL Ocean Freight',
        air: 'Air Freight'
      },

      // Price methods
      priceMethods: {
        direct: 'Direct',
        branch: 'Branch',
        seaRail: 'Sea-Rail'
      },

      // Status
      status: {
        enabled: 'Enabled',
        disabled: 'Disabled'
      },

      // Filter fields
      filters: {
        rulesId: 'Rule ID',
        rulesType: 'Rule Type',
        laneIds: 'Lane',
        carrierIds: 'Carrier',
        pols: 'Port of Loading',
        pods: 'Port of Discharge',
        priceMethod: 'Price Method',
        chargeItemId: 'Fee Name',
        currency: 'Currency',
        measure: 'Billing Unit',
        enableFlag: 'Enable Status',
        branchLines: 'Branch Line Type',
        dateCreated: 'Created Date',
        dateModified: 'Modified Date',
        createdBy: 'Created By',
        modifiedBy: 'Modified By'
      },

      // Placeholders
      placeholders: {
        rulesId: 'Please enter rule ID',
        rulesType: 'Please select rule type',
        laneIds: 'Please select lane',
        carrierIds: 'Please select carrier',
        pols: 'Please select port of loading',
        pods: 'Please select port of discharge',
        priceMethod: 'Please select price method',
        chargeItemId: 'Please select fee name',
        currency: 'Please select currency',
        measure: 'Please select billing unit',
        enableFlag: 'Please select enable status',
        branchLines: 'Please select branch line type',
        dateCreated: 'Please select created date range',
        dateModified: 'Please select modified date range',
        createdBy: 'Please enter created by',
        modifiedBy: 'Please enter modified by'
      },

      // Table columns
      columns: {
        rulesId: 'Rule ID',
        rulesType: 'Rule Type',
        laneIds: 'Lane',
        carrierIds: 'Carrier',
        pols: 'Port of Loading',
        pods: 'Port of Discharge',
        priceMethod: 'Price Method',
        chargeItemId: 'Fee Name',
        currency: 'Currency',
        measure: 'Billing Unit',
        enableFlag: 'Status',
        dateCreated: 'Created Date',
        dateModified: 'Modified Date',
        createdBy: 'Created By',
        modifiedBy: 'Modified By',
        branchLines: 'Branch Line Type',
        actions: 'Actions'
      },

      // Action buttons
      actions: {
        search: 'Search',
        reset: 'Reset',
        add: 'Add Rule',
        view: 'Details',
        edit: 'Edit',
        copy: 'Copy',
        more: 'More',
        enable: 'Enable',
        disable: 'Disable',
        batchOperations: 'Batch Operations',
        batchEnable: 'Batch Enable',
        batchDisable: 'Batch Disable',
        delete: 'Delete'
      },

      // Batch operations
      batch: {
        selected: 'Selected {count} items',
        enableConfirm: 'Are you sure you want to enable the selected {count} rules?',
        disableConfirm: 'Are you sure you want to disable the selected {count} rules?',
        deleteConfirm: 'Are you sure you want to delete the selected {count} rules? This action cannot be undone.',
        enableSuccess: 'Successfully enabled {count} rules',
        disableSuccess: 'Successfully disabled {count} rules',
        deleteSuccess: 'Successfully deleted {count} rules',
        selectFirst: 'Please select rules to operate on first'
      },

      // Messages
      messages: {
        loadDataFailed: 'Failed to load data',
        loadDataError: 'Failed to load increase rules data:',
        copySuccess: 'Rule information has been copied to the new page. Rule ID will be regenerated. Please confirm information before saving',
        batchEnableFailed: 'Batch enable failed',
        batchDisableFailed: 'Batch disable failed',
        batchDeleteFailed: 'Batch delete failed',
        enableSuccess: 'Rule has been enabled',
        disableSuccess: 'Rule has been disabled',
        statusToggleFailed: 'Status toggle failed',
        deleteConfirm: 'Are you sure you want to delete this increase rule?',
        deleteSuccess: 'Delete successful',
        deleteFailed: 'Delete failed',
        totalCount: 'Total {count} items',
        unknown: 'Unknown',
        exportFilename: 'Increase Rules List'
      },

      // Detail page
      detail: {
        // Section titles
        sections: {
          basicInfo: 'Basic Information',
          ruleScope: 'Rule Scope',
          chargeInfo: 'Fee Information'
        },

        // Page action buttons
        pageActions: {
          back: 'Back',
          save: 'Save',
          saveAndPublish: 'Save and Publish',
          edit: 'Edit',
          copy: 'Copy',
          copyAlert: 'Copying rule information. Rule ID will be automatically regenerated. Please confirm all information before saving'
        },

        // Basic information
        basicInfo: {
          title: 'Basic Information',
          rulesId: 'Rule ID',
          rulesIdPlaceholder: 'Auto-generated by backend, format: IRRYYMMDD0001',
          rulesIdCopyPlaceholder: 'Will be regenerated after copying, format: IRRYYMMDD0001',
          rulesType: 'Rule Type',
          rulesTypeRequired: 'Please select rule type',
          rulesTypePlaceholder: 'Please select rule type',
          priceMethod: 'Price Method',
          priceMethodRequired: 'Please select price method',
          priceMethodPlaceholder: 'Please select price method'
        },

        // Rule scope
        ruleScope: {
          title: 'Rule Scope',
          laneIds: 'Lane',
          laneIdsRequired: 'Please select at least one lane',
          laneIdsPlaceholder: 'Please select lane',
          carrierIds: 'Carrier',
          carrierIdsRequired: 'Please select at least one carrier',
          carrierIdsPlaceholder: 'Please select carrier',
          pols: 'Port of Loading',
          polsRequired: 'Please select at least one port of loading',
          polsPlaceholder: 'Please select port of loading',
          pods: 'Port of Discharge',
          podsRequired: 'Please select at least one port of discharge',
          podsPlaceholder: 'Please select port of discharge',
          branchLines: 'Branch Line Type',
          branchLinesPlaceholder: 'Please select branch line type'
        },

        // Fee information
        chargeInfo: {
          title: 'Fee Information',
          chargeItemId: 'Fee Name',
          chargeItemIdRequired: 'Please select fee name',
          chargeItemIdPlaceholder: 'Please select fee name',
          currency: 'Currency',
          currencyRequired: 'Please select currency',
          currencyPlaceholder: 'Please select currency',
          measure: 'Billing Unit',
          measureRequired: 'Please select billing unit',
          measurePlaceholder: 'Please select billing unit',
          enableFlag: 'Enable Status',
          enableFlagRequired: 'Please select enable status',
          enableFlagPlaceholder: 'Please select enable status'
        },

        // Form validation
        validation: {
          rulesTypeRequired: 'Please select rule type',
          priceMethodRequired: 'Please select price method',
          laneIdsRequired: 'Please select at least one lane',
          carrierIdsRequired: 'Please select at least one carrier',
          polsRequired: 'Please select at least one port of loading',
          podsRequired: 'Please select at least one port of discharge',
          chargeItemIdRequired: 'Please select fee name',
          currencyRequired: 'Please select currency',
          measureRequired: 'Please select billing unit'
        }
      }
    },
    lastmile: {
      title: 'Last Mile Rate Management',
      description: 'Manage last mile rate information',
      deliveryAddressType: {
        thirdParty: 'Third Party Address',
        amazonWarehouse: 'Amazon Warehouse',
        easyWarehouse: 'Easy Warehouse',
        unknown: 'Unknown'
      },
      regionSelection: {
        title: 'Region',
        detailedAddress: 'Detail Address',
        postalCodeLabel: 'Postal Code:'
      },
      pageTitles: {
        create: 'Add Last Mile Rate',
        edit: 'Edit Last Mile Rate',
        detail: 'Last Mile Rate Details',
        copy: 'Copy Last Mile Rate'
      },
      filters: {
        priceMethod: 'Rate Type',
        status: 'Status',
        destinationPort: 'POD',
        agent: 'Agent Name',
        currency: 'Currency',
        feeName: 'Fee Name',
        unit: 'Unit',
        addressType: 'Address Type',
        country: 'Country',
        postalCode: 'Postal Code',
        state: 'State',
        city: 'City',
        district: 'District',
        warehouseCode: 'Warehouse Code'
      },
      placeholders: {
        priceMethod: 'Select rate type',
        status: 'Select status',
        destinationPort: 'Select POD',
        agent: 'Enter agent name',
        currency: 'Select currency',
        feeName: 'Select fee name',
        unit: 'Select unit',
        addressType: 'Select address type',
        country: 'Select country',
        postalCode: 'Select postal code',
        state: 'Select state',
        city: 'Select city',
        district: 'Select district',
        warehouseCode: 'Enter warehouse code'
      },
      priceMethods: {
        direct: 'Direct',
        branch: 'Branch',
        seaRail: 'Sea-Rail'
      },
      status: {
        draft: 'Draft',
        normal: 'Normal',
        expired: 'Expired',
        offline: 'Offline',
        unknown: 'Unknown'
      },
      columns: {
        priceNo: 'Last Mile Rate No.',
        pods: 'POD',
        deliveryAddressType: 'Address Type',
        postalCode: 'Postal Code',
        address: 'Address',
        warehouseCode: 'Warehouse Code',
        agentName: 'Agent Name',
        gp20: '20GP',
        gp40: '40GP',
        hc20: '20HC',
        hc40: '40HC',
        hc45: '45HC',
        nor20: '20NOR',
        nor40: '40NOR',
        tk20: '20TK',
        tk40: '40TK',
        ot20: '20OT',
        ot40: '40OT',
        fr20: '20FR',
        fr40: '40FR',
        validPeriod: 'Valid Period',
        status: 'Status',
        createdBy: 'Created By',
        dateCreated: 'Create Date',
        modifiedBy: 'Modified By',
        modifyDate: 'Modify Date',
        remarks: 'Remarks',
        actions: 'Actions'
      },
      actions: {
        search: 'Search',
        reset: 'Reset',
        import: 'Import',
        add: 'Add Last Mile Rate',
        view: 'View',
        edit: 'Edit',
        copy: 'Copy',
        more: 'More',
        onShelf: 'On Shelf',
        offShelf: 'Off Shelf',
        delete: 'Delete',
        batchOperations: 'Batch Operations',
        batchOnShelf: 'Batch On Shelf',
        batchOffShelf: 'Batch Off Shelf',
        batchDelete: 'Batch Delete',
        batchPriceChange: 'Batch Price Change',
        aiRecognition: 'AI Recognition',
        batchValidityChange: 'Batch Validity Change',
        booking: 'Book Now',
        rateTrend: 'Rate Trend',
        availableSailings: 'Available Sailings'
      },
      messages: {
        loadFailed: 'Failed to load data',
        loadMoreFailed: 'Failed to load data, please try again later',
        featureDeveloping: '{feature} feature is under development...',
        exportFilename: 'Last Mile Rate Data'
      }
    }
  },

  // Surcharge Management Module
  surchargeManagement: {
    // Page title
    title: 'Surcharge Management',
    description: 'Manage various surcharge information',

    // Tab navigation
    tabs: {
      fcl: 'FCL Surcharge'
    },

    columns: {
      feeNo: 'Surcharge ID',
      feeStatus: 'Fee Status',
      pol: 'Port of Loading',
      carrier: 'Carrier',
      lane: 'Route',
      validPeriod: 'Valid Period',
      createdBy: 'Created By',
      modifiedBy: 'Modified By',
      dateCreated: 'Created Date',
      dateModified: 'Modified Date',
      actions: 'Actions'
    },

    // Filter field labels
    filterLabels: {
      feeNo: 'Fee ID',
      feeStatus: 'Fee Status',
      pol: 'Port of Loading',
      carrier: 'Carrier',
      lane: 'Route',
      validPeriod: 'Valid Period',
      beginDate: 'Effective Date',
      endDate: 'Expiry Date',
      createdBy: 'Created By',
      dateCreated: 'Created Date',
      modifiedBy: 'Modified By',
      dateModified: 'Modified Date'
    },

    statusEnum: {
      1: 'Disabled',
      2: 'Enabled'
    },

    fcl: {
      // Page title
      title: 'FCL Surcharge Management',
      description: 'Manage FCL surcharge information',

      // Action buttons
      actions: {
        add: 'Add FCL Surcharge',
        import: 'Import',
        batchOperations: 'Batch Operations',
        batchEnable: 'Batch Enable',
        batchDisable: 'Batch Disable',
        batchDelete: 'Batch Delete',
        view: 'View',
        edit: 'Edit',
        more: 'More',
        copy: 'Copy',
        enable: 'Enable',
        disable: 'Disable',
        delete: 'Delete'
      },

      // Batch operations
      batch: {
        selected: 'Selected {count} items',
        enableConfirm: 'Are you sure to enable the selected {count} surcharges?',
        disableConfirm: 'Are you sure to disable the selected {count} surcharges?',
        deleteConfirm: 'Are you sure to delete the selected {count} surcharges? This action cannot be undone.',
        enableSuccess: 'Successfully enabled {count} surcharges',
        disableSuccess: 'Successfully disabled {count} surcharges',
        deleteSuccess: 'Successfully deleted {count} surcharges'
      },

      // Messages
      messages: {
        enableSuccess: 'Enable successful',
        enableFailed: 'Enable failed',
        disableSuccess: 'Disable successful',
        disableFailed: 'Disable failed',
        batchEnableSuccess: 'Batch enable successful',
        batchEnableFailed: 'Batch enable failed',
        batchDisableSuccess: 'Batch disable successful',
        batchDisableFailed: 'Batch disable failed',
        statusSwitchFailed: 'Status switch failed, please try again',
        onlyDraftCanEnable: 'Only draft status surcharges can be enabled, current selection includes: {status} status surcharges',
        onlyNormalCanDisable: 'Only normal status surcharges can be disabled, current selection includes: {status} status surcharges',
        importFeature: 'Import feature is under development',
        aiRecognitionDemo: 'AI recognition feature demo',
        missingRecordId: 'Cannot view details: Missing record ID',
        missingEditId: 'Cannot edit: Missing record ID',
        loadDataFailed: 'Failed to load data',
        copyDataFailed: 'Failed to load copy data',
        draftSaveSuccess: 'Draft saved successfully',
        draftSaveFailed: 'Failed to save draft',
        unknown: 'Unknown error',
        noBaseFeeData: 'No base fee data available',
        saveDataFirst: 'Please save data first'
      },

      // Detail page
      detail: {
        // Page titles
        createTitle: 'Add FCL Surcharge',
        editTitle: 'Edit FCL Surcharge',
        viewTitle: 'Surcharge Details',
        copyTitle: 'Copy FCL Surcharge',

        // Basic information
        basicInfo: 'Basic Information',
        pol: 'Port of Loading',
        carrier: 'Carrier',
        feeStatus: 'Fee Status',
        lane: 'Route',
        validPeriod: 'Valid Period',
        beginDate: 'Start Date',
        endDate: 'End Date',

        // Price details
        priceDetails: 'Price Details',
        byContainerType: 'By Container Type',
        notByContainerType: 'Not By Container Type',
        containerTypeSettings: 'Container Type Settings',
        addItem: 'Add Item',
        baseFee: 'Base Fee',

        // Fee items
        feeItem: 'Fee Name',
        autoFill: 'Auto Fill',
        billingUnit: 'Billing Unit',
        unitPrice: 'Unit Price',
        specialRemarks: 'Special Remarks',

        // Table column titles
        tableColumns: {
          feeName: 'Fee Name',
          currency: 'Currency',
          specialRemarks: 'Special Remarks',
          actions: 'Actions',
          billingUnit: 'Billing Unit',
          unitPrice: 'Unit Price',
          baseFee: 'Base Fee'
        },

        // Operation buttons
        operationButtons: {
          addItem: 'Add Item',
          delete: 'Delete'
        },

        // Billing types
        billingTypes: {
          byContainerType: 'By Container Type',
          notByContainerType: 'Not By Container Type'
        },

        // Tips
        tips: {
          noData: 'No data, click add button to add fee items',
          basicFeeTag: 'Base Fee',
          autoFillCurrency: 'Auto Fill'
        },

        // Container type settings
        selectContainerTypes: 'Select container types to display in the price table:',

        // AI recognition
        aiRecognition: 'AI Intelligent Recognition',
        uploadFile: 'Upload Rate File',
        dragAndDrop: 'Click or drag files to this area to upload',
        supportedFormats: 'Supported formats: PDF, Excel, Word, images, etc.',
        selectFile: 'Select File',
        aiDescription: 'AI Recognition Description',
        aiFeatures: [
          '• AI will automatically recognize ports, carriers, prices and other information in rate tables',
          '• Supports multiple file formats and complex table structures',
          '• After recognition is complete, it will automatically fill in the corresponding fields',
          '• Please ensure the uploaded file is clear and readable'
        ],

        // Copy tip
        copyTip: 'Copying rate information, rate number will be automatically regenerated, please confirm all information before saving',

        // Placeholders
        placeholders: {
          selectPol: 'Please select port of loading',
          selectCarrier: 'Please select carrier',
          selectFeeStatus: 'Please select fee status',
          selectLane: 'Please select route',
          selectFeeItem: 'Please select fee name',
          selectBillingUnit: 'Please select billing unit',
          enterPrice: 'Please enter price',
          enterUnitPrice: 'Please enter unit price',
          enterRemarks: 'Please enter special remarks',
          feeNoGenerated: 'Backend auto-generated, format: 73F75AN6',
          feeNoCopy: 'Will be regenerated after copying, format: 73F75AN6'
        },

        // Validation messages
        validation: {
          selectPolRequired: 'Please select port of loading',
          selectCarrierRequired: 'Please select carrier',
          selectFeeStatusRequired: 'Please select fee status',
          selectLaneRequired: 'Please select at least one route',
          selectValidPeriodRequired: 'Please select valid period',
          selectFeeItemRequired: 'Please select fee name',
          selectBillingUnitRequired: 'Please select billing unit',
          enterPriceRequired: 'Please enter price',
          enterUnitPriceRequired: 'Please enter unit price'
        },

        // Status text
        status: {
          normal: 'Normal',
          disabled: 'Disabled'
        },

        // Buttons
        buttons: {
          back: 'Back',
          save: 'Save',
          edit: 'Edit',
          copy: 'Copy'
        }
      }
    }
  },

  // Inquiry Detail Page
  inquiryDetail: {
    // Page titles
    pageTitle: {
      create: 'New Inquiry',
      edit: 'Inquiry Detail',
      view: 'Inquiry Detail',
      loading: 'Loading...'
    },

    // Form sections
    sections: {
      basicInfo: 'Basic Information',
      cargoInfo: 'Cargo Information',
      loadingPoint: 'Loading Point',
      deliveryAddress: 'Last Mile Delivery Address',
      containerInfo: 'Container Type & Quantity',
      matchPrice: 'Matched Prices',
      fclPrice: 'FCL Prices',
      prePortPrice: 'Preport Prices',
      lastMilePrice: 'Last Mile Prices'
    },

    // Checkbox options
    checkboxes: {
      prePortPrice: 'Preport Price',
      trunkLinePrice: 'Trunk Line Price',
      lastMilePrice: 'Last Mile Price'
    },

    // Attachments
    attachments: {
      title: 'Attachments',
      titleRequired: 'Attachments *',
      tip: 'Supports uploading documents, images, compressed files, etc. Max 20MB per file, up to 20 files.'
    },

    // Action buttons
    actions: {
      saveDraft: 'Save Draft',
      exportRate: 'Export Rate',
      exportQuotation: 'Export Quotation',
      submit: 'Submit',
      cancel: 'Cancel',
      back: 'Back',
      aiRecognition: 'AI Recognition',
      addContainerType: 'Add Container Type',
      matchPrice: 'Query Matched Prices'
    },

    // Basic information fields
    basicInfo: {
      inquiryNo: 'Inquiry No.',
      inquirerId: 'Inquirer',
      palletNature: 'Pallet Nature',
      directTransit: 'Transport Type',
      cargoReadyTime: 'Cargo Ready Time',
      palletQuality: 'Pallet Quality',
      serviceTerm: 'Service Term',
      client: 'Client',
      detailAddress: 'Detail Address'
    },

    // Cargo information fields
    cargoInfo: {
      directTransit: 'Direct/Transit',
      route: 'Route',
      loadingPort: 'Loading Port',
      destinationPort: 'Destination Port',
      dischargingPort: 'Discharging Port',
      transitPort1: 'Transit Port (1st)',
      transitPort2: 'Transit Port (2nd)',
      transitPort3: 'Transit Port (3rd)',
      cargoType: 'Cargo Type',
      hazardClass: 'Hazard Class',
      unNo: 'UN No.',
      temperature: 'Temperature',
      ventilation: 'Ventilation',
      quantity: 'Quantity',
      weight: 'Weight',
      dimensions: 'Dimensions',
      cargoDescription: 'Cargo Description (HS Code)',
      containerRequirement: 'Container Requirement',
      transportTerms: 'Transport Terms',
      cargoValue: 'Cargo Value',
      cargoValueCurrency: 'Currency',
      carrier: 'Carrier',
      remarks: 'Remarks'
    },

    // Loading point fields
    loadingPoint: {
      selectArea: 'Select Loading Point Area',
      province: 'Province',
      city: 'City',
      district: 'District',
      street: 'Street/Town',
      detailAddress: 'Detail Address'
    },

    // Delivery address fields
    deliveryAddress: {
      addressInfo: 'Delivery Address Information',
      addressType: 'Delivery Address Type',
      postalCodeAndCountry: 'Postal Code & Country',
      country: 'Select country',
      postalCode: 'Enter or select postal code',
      provinceCity: 'Province/State/City',
      province: 'Province/State',
      city: 'City',
      district: 'District',
      detailAddress: 'Detail Address',
      amazonWarehouse: 'Amazon Warehouse Selection',
      ecangWarehouse: 'Ecang Warehouse Selection'
    },

    // Container information fields
    containerInfo: {
      containerType: 'Container Type',
      quantity: 'Quantity',
      containerTypeAndQuantity: 'Container Type & Quantity: ',
      rfTemperatureRequirement: 'Temperature Requirement',
      rfVentilationRequirement: 'Ventilation Requirement',
      temperatureRange: 'Enter temperature range, e.g.: -20 to 30',
      temperatureStart: 'Start Temperature',
      temperatureEnd: 'End Temperature',
      ventilationDoor: 'Ventilation Door',
      ventilationOpen: 'Open',
      ventilationClose: 'Close',
      ventilationVolume: 'Air Flow',
      ventilationVolumeUnit: 'CBM/H',
      ventilationVolumePlaceholder: 'Enter air flow',
      humidity: 'Humidity',
      humidityPlaceholder: 'Enter humidity',
      humidityUnit: '%RH',
      needPrecooling: 'Pre-cooling Required',
      needPrecoolingYes: 'Yes',
      needPrecoolingNo: 'No',
      temperatureUnit: '℃',
      temperatureTo: 'to',
      cargoQuantity: 'Pieces',
      weight: 'Weight',
      weightUnit: 'Weight Unit',
      dimensions: 'Dimensions',
      dimensionsUnit: 'Dimension Unit',
      length: 'L',
      width: 'W',
      height: 'H'
    },

    // Placeholder texts
    placeholders: {
      autoGenerate: 'Auto Generate',
      selectPalletNature: 'Select pallet nature',
      selectCargoReadyTimeRange: 'Select cargo ready time range',
      selectCargoReadyDate: 'Select cargo ready date',
      selectOption: 'Please select',
      inputServiceTerm: 'Enter service term',
      selectClientType: 'Select client type',
      selectClient: 'Select client',
      inputClientTitle: 'Enter client title',
      selectRoute: 'Select route',
      selectLoadingPort: 'Select loading port',
      selectDestinationPort: 'Select destination port',
      selectDischargePort: 'Select discharge port',
      selectTransitPort1: 'Select first transit port',
      selectTransitPort2: 'Select second transit port',
      selectTransitPort3: 'Select third transit port',
      selectHazardClass: 'Select hazard class',
      inputUnNo: 'Enter UN No.',
      inputTemperature: 'Enter temperature',
      inputVentilation: 'Enter ventilation',
      enterInputQuantity: 'Enter quantity',
      inputWeight: 'Enter weight',
      inputDimensions: 'Enter dimensions',
      inputCargoDescription: 'Enter cargo description or HS Code',
      selectContainerRequirement: 'Select container requirement',
      selectTransportTerms: 'Select transport terms',
      inputCargoValue: 'Enter cargo value',
      selectCargoValueCurrency: 'Select currency',
      selectCarrier: 'Select carrier',
      inputRemarks: 'Enter remarks',
      province: 'Province',
      city: 'City',
      district: 'District',
      street: 'Street/Town',
      inputDetailAddress: 'Enter detail address',
      selectCountry: 'Select country',
      inputPostalCode: 'Enter or select postal code',
      inputDeliveryAddress: 'Enter detail address',
      selectAmazonWarehouse: 'Select Amazon warehouse',
      selectEcangWarehouse: 'Select Ecang warehouse',
      selectContainerType: 'Select container type',
      inputQuantity: 'Quantity',
      inputLoadingPointAddress: 'Enter loading point address',
      inputDeliveryAddressText: 'Enter delivery address',
      clickMatchPrice: 'Please click the "Query Matched Prices" button to get the price information',
      noFclMatches: 'No matching FCL prices found',
      noPrePortMatches: 'No matching preport prices found',
      noLastMileMatches: 'No matching last mile prices found',
      noFclPrices: 'No FCL prices',
      noPrePortPrices: 'No Preport prices',
      noLastMilePrices: 'No Last mile prices',
      quotePending: 'Quote pending',
      quoteRejected: 'Quote rejected',
      noFeeDetail: 'No fee detail'
    },

    // Units and suffixes
    units: {
      celsius: '°C',
      kilogram: 'KGS'
    },

    // AI recognition modal
    aiModal: {
      loadingPointTitle: 'AI Smart Address Recognition',
      deliveryTitle: 'AI Smart Address Recognition',
      loadingPointDesc: 'Please paste the address information into the text box below:',
      deliveryDesc: 'Please paste the US address information into the text box below:',
      loadingPointTip: 'Tip: The system will automatically parse province, city, district and street information, detail address will be filled into the detail address field',
      deliveryRules: 'Recognition Rules:',
      deliveryRule1: 'If contains Amazon warehouse code (ONT8/BFI4 etc.), will be set as Amazon warehouse type',
      deliveryRule2: 'If contains Ecang code (LAX203/ATL205 etc.), will be set as Ecang type',
      deliveryRule3: 'Other cases will be set as third-party address, and parse address and postal code',
      recognize: 'Recognize',
      cancel: 'Cancel'
    },

    // Dropdown options
    options: {
      alibaba: 'Alibaba Group',
      jd: 'JD Logistics Co., Ltd.',
      huawei: 'Huawei Technologies Co., Ltd.',
      xiaomi: 'Xiaomi Corporation',
      haier: 'Haier Group Corporation',
      baosteel: 'Baosteel Group Corporation Limited',
      cmg: 'China Merchants Group',
      cosco: 'COSCO SHIPPING Holdings'
    },

    // Messages
    messages: {
      saveDraftSuccess: 'Draft saved successfully',
      updateSuccess: 'Inquiry updated successfully',
      submitSuccess: 'Inquiry submitted successfully',
      saveFailed: 'Save failed',
      submitFailed: 'Submit failed',
      exportPending: 'Export rate function under development...',
      addressRecognitionSuccess: 'Address recognition successful',
      inputAddressFirst: 'Please enter address information',
      confirmSubmit: 'Confirm to submit inquiry?',
      confirm: 'Confirm',
      cancel: 'Cancel',
      loading: 'Loading...',
      noData: 'No data',
      copySuccess: 'Text copied to clipboard',
      copyFailed: 'Copy failed, please copy manually',
      noAvailablePrices: 'No available price information found. Please query and select matched prices, or wait for new quotations.',
      selectQuotedPriceFirst: 'Please select a quoted price first',
      loadTemplateFailed: 'Failed to load template',
      openTemplateViewReportFailed: 'Failed to open template view report',
      inquiryAllDisabled: 'At least one of trunk line, pre-port and last mile prices must be enabled',
      routeMatchSuccess: 'Route matched successfully',
      routeMatchFailed: 'No matching route found',
      addressRecognitionFailed: 'Address recognition failed',
      cargoDescriptionRequired: 'Cargo description is required when transport terms end with DOOR',
      cargoValueRequired: 'Cargo value is required when service term is DDP or transport terms end with DOOR',
      cargoValueCurrencyRequired: 'Cargo value currency is required when service term is DDP or transport terms end with DOOR',
      rfTemperatureStartRequired: 'Start temperature is required',
      rfTemperatureEndRequired: 'End temperature is required',
      rfVentilationEnableRequired: 'Ventilation door is required',
      rfVentilationQuantityRequired: 'Air flow is required',
      loadingPortSameAsDestination: 'Loading port and destination port cannot be the same',
      destinationPortSameAsLoading: 'Destination port and loading port cannot be the same'
    },

    // Export quotation modal
    exportQuotationModal: {
      title: 'Export Quotations',
      containerTypeAndQuantity: 'Container Type & Quantity',
      copyQuotationText: 'Copy Quick Quotation Text',
      printQuotation: 'Print Quotation Document',
      printPending: 'Print function under development'
    },

    // Quick quotation text modal
    quotationTextModal: {
      title: 'Quick Quotation Text',
      description: 'The following is the quotation text generated based on your selected container types and quantities. You can copy and send it to customers:',
      placeholder: 'Please enter quotation text',
      close: 'Close',
      copyText: 'Copy Text'
    },

    // Quotation text content
    quotationText: {
      title: '【Inquiry Quotations】',
      inquiryNo: 'Inquiry No.: ',
      inquiryType: 'Inquiry Type: ',
      fcl: 'FCL',
      prePort: 'Pre port',
      lastMile: 'Last mile',
      inquirer: 'Inquirer: ',
      containerTypeAndQuantity: 'Container Type & Quantity: ',
      routeInfo: 'Route Information: ',
      loadingPort: 'Loading Port: ',
      destinationPort: 'Destination Port: ',
      carrier: 'Carrier: ',
      directTransit: 'Direct/Transit: ',
      priceDetails: 'Price Details: ',
      trunkLineFee: 'Trunk Line Fee: ',
      prePortFee: 'Pre-port Fee: ',
      lastMileFee: 'Last Mile Fee: ',
      feeCalculation: 'Fee Calculation: ',
      trunkLine: 'Trunk Line: ',
      cargoReadyTime: 'Cargo Ready Time: ',
      client: 'Client: ',
      remarks: 'Remarks: ',
      priceDisclaimer: '※ The above prices are for reference only, actual prices are subject to formal contracts',
      contactInfo: '※ If you have any questions, please contact our customer service team',
      notSpecified: 'Not Specified',
      containerCharges: 'Container Charges: ',
      otherCharges: 'Other Charges: '
    },

    // Price display labels
    priceDisplay: {
      priceNo: 'Price No',
      carrierName: 'Carrier',
      validPeriod: 'Valid Period',
      directTransit: 'Direct/Transit',
      direct: 'Direct',
      transit: 'Transit',
      voyageDays: 'Voyage Days',
      days: 'day(s)',
      detentionFreeDays: 'Free Detention Days',
      demurrageFreeDays: 'Free Demurrage Days',
      containerCharges: 'Container Charges',
      otherCharges: 'Other Charges',
      supplier: 'Supplier',
      priceMethod: 'Price Method',
      agentName: 'Agent Name',
      basicSeaFee: 'Basic Ocean Freight',
      surcharge: 'Surcharge'
    },

    // ZIP code related
    zipCode: {
      loading: 'Loading...',
      loadProvincesFailed: 'Failed to load provinces',
      loadCitiesFailed: 'Failed to load cities',
      loadDistrictsFailed: 'Failed to load districts',
      loadStreetsFailed: 'Failed to load streets',
      loadZipCodesFailed: 'Failed to load postal codes',
      zipCodeQueryFailed: 'Postal code query failed',
      zipCodeLoadSuccess: 'China postal codes loaded successfully',
      loadContainerTypesFailed: 'Failed to load container types',
      containerTypesLoadSuccess: 'Container types loaded successfully'
    },

    // Table columns
    columns: {
      feeName: 'Fee Name',
      currency: 'Currency',
      unitRate: 'Unit Rate',
      measure: 'Measure',
      remarks: 'Remarks'
    }
  },

  // Quotation Detail Page
  quotationDetail: {
    // Page titles
    pageTitle: {
      create: 'New Quotation',
      edit: 'Quotation Detail',
      view: 'Quotation Detail',
      loading: 'Loading...'
    },

    // Form sections
    sections: {
      basicInfo: 'Basic Information',
      cargoInfo: 'Cargo Information',
      loadingPoint: 'Loading Point',
      deliveryAddress: 'Last Mile Delivery Address',
      containerInfo: 'Container Type & Quantity',
      fclPrice: 'FCL Prices',
      prePortPrice: 'Preport Prices',
      lastMilePrice: 'Last Mile Prices',
      priceDetail: 'Price Details',
      trunkLinePrice: 'Trunk Line Price',
      addTrunkLinePrice: 'Add Trunk Line Price',
      addPrePortPrice: 'Add Preport Price',
      addLastMilePrice: 'Add Last Mile Price',
      rejectQuote: 'Reject Quote',
      rejectReason: 'Please input the reason for rejection',
      feeDetail: 'Fee Details',
      nonContainerFeeItem: 'Non-Container Fee Item',
      containerFeeItem: 'Container Fee Item',
      addNonContainerFeeItem: 'Add Non-Container Fee Item',
      addContainerFeeItem: 'Add Container Fee Item'
    },

    // Checkbox options
    checkboxes: {
      prePortPrice: 'Preport Price',
      trunkLinePrice: 'Trunk Line Price',
      lastMilePrice: 'Last Mile Price'
    },

    // Action buttons
    actions: {
      saveDraft: 'Save Draft',
      exportQuotation: 'Export Quotation',
      submit: 'Submit',
      cancel: 'Cancel',
      back: 'Back',
      delete: 'Delete',
      confirmReject: 'Confirm Reject',
      confirmQuote: 'Confirm Quote',
      revokeQuote: 'Revoke Quote',
      aiRecognition: 'AI Recognition',
      addContainerType: 'Add Container Type',
      bookingNow: 'Book Now'
    },

    // Temporary Markup (view-only, not persisted)
    tempMarkup: {
      action: 'Temp Markup',
      modalTitle: 'Set Temporary Markup',
      feeItem: 'Fee Item',
      boxType: 'Box Type',
      originalPrice: 'Original Price',
      amountLabel: 'Temporary markup amount (negative for discount)',
      amountPlaceholder: 'Enter markup amount',
      tipsSignRule: 'Tip: Positive = markup, Negative = discount',
      adjustedPrice: 'Adjusted Price',
      importantNotes: 'Important Notes:',
      note1: 'Temporary markup is for quotation view only and will not alter source data',
      note2: 'Adjusted price will be shown as "original ± markup" in the table',
      note3: 'You can modify or clear the temporary markup anytime',
      saveSuccess: 'Temporary markup saved',
      clearSuccess: 'Temporary markup cleared'
    },

    // Basic information fields
    basicInfo: {
      quotationNo: 'Quotation No.',
      inquiryNo: 'Inquiry No.',
      quoterId: 'Quoter',
      palletNature: 'Pallet Nature',
      cargoReadyTime: 'Cargo Ready Time',
      client: 'Client',
      inquiryEnterprise: 'Inquiry Enterprise',
      detailAddress: 'Detail Address'
    },

    // Cargo information fields
    cargoInfo: {
      directTransit: 'Direct/Transit',
      route: 'Route',
      loadingPort: 'Loading Port',
      destinationPort: 'Destination Port',
      dischargingPort: 'Discharging Port',
      transitPort1: 'Transit Port (1st)',
      transitPort2: 'Transit Port (2nd)',
      transitPort3: 'Transit Port (3rd)',
      quantity: 'Quantity',
      weight: 'Weight',
      dimensions: 'Dimensions',
      containerRequirement: 'Container Requirement',
      transportTerms: 'Transport Terms',
      cargoValue: 'Cargo Value',
      cargoValueCurrency: 'Currency',
      carrier: 'Carrier',
      remarks: 'Remarks'
    },

    // Loading point fields
    loadingPoint: {
      selectArea: 'Select Loading Point Area',
      province: 'Province',
      city: 'City',
      district: 'District',
      street: 'Street/Town',
      detailAddress: 'Detail Address',
      administrativeDivision: 'Administrative Division',
      detailedLocation: 'Detailed Location'
    },

    // Delivery address fields
    deliveryAddress: {
      addressInfo: 'Delivery Address Information',
      addressType: 'Delivery Address Type',
      postalCodeAndCountry: 'Postal Code & Country',
      country: 'Select country',
      postalCode: 'Enter or select postal code',
      provinceCity: 'Province/State/City',
      province: 'Province/State',
      city: 'City',
      district: 'District',
      detailAddress: 'Detail Address',
      amazonWarehouse: 'Amazon Warehouse Selection',
      ecangWarehouse: 'Ecang Warehouse Selection',
      thirdPartyAddress: 'Third Party Address'
    },

    // Container information fields
    containerInfo: {
      containerType: 'Container Type',
      quantity: 'Quantity',
      containerTypeAndQuantity: 'Container Type & Quantity: ',
      rfTemperatureRequirement: 'Temperature Requirement',
      rfVentilationRequirement: 'Ventilation Requirement',
      temperatureRange: 'Enter temperature range, e.g.: -20 to 30',
      temperatureStart: 'Start Temperature',
      temperatureEnd: 'End Temperature',
      ventilationDoor: 'Ventilation Door',
      ventilationOpen: 'Open',
      ventilationClose: 'Close',
      ventilationVolume: 'Air Flow',
      ventilationVolumeUnit: 'CBM/H',
      ventilationVolumePlaceholder: 'Enter air flow',
      temperatureUnit: '℃',
      temperatureTo: 'to',
      cargoQuantity: 'Pieces',
      weight: 'Weight',
      weightUnit: 'Weight Unit',
      dimensions: 'Dimensions',
      dimensionsUnit: 'Dimension Unit',
      length: 'L',
      width: 'W',
      height: 'H'
    },

    // Placeholder texts
    placeholders: {
      autoGenerate: 'Auto Generate',
      selectPalletNature: 'Select pallet nature',
      selectCargoReadyTimeRange: 'Select cargo ready time range',
      selectCargoReadyDate: 'Select cargo ready date',
      selectOption: 'Please select',
      inputServiceTerm: 'Enter service term',
      selectClientType: 'Select client type',
      selectClient: 'Select client',
      inputClientTitle: 'Enter client title',
      selectRoute: 'Select route',
      selectLoadingPort: 'Select loading port',
      selectDestinationPort: 'Select destination port',
      selectDischargingPort: 'Select discharging port',
      selectTransitPort1: 'Select first transit port',
      selectTransitPort2: 'Select second transit port',
      selectTransitPort3: 'Select third transit port',
      enterInputQuantity: 'Enter quantity',
      inputWeight: 'Enter weight',
      inputDimensions: 'Enter dimensions',
      selectContainerRequirement: 'Select container requirement',
      selectTransportTerms: 'Select transport terms',
      inputCargoValue: 'Enter cargo value',
      selectCargoValueCurrency: 'Select currency',
      selectCarrier: 'Select carrier',
      inputRemarks: 'Enter remarks',
      province: 'Province',
      city: 'City',
      district: 'District',
      street: 'Street/Town',
      inputDetailAddress: 'Enter detail address',
      selectCountry: 'Select country',
      inputPostalCode: 'Enter or select postal code',
      inputDeliveryAddress: 'Enter detail address',
      selectAmazonWarehouse: 'Select Amazon warehouse',
      selectEcangWarehouse: 'Select Ecang warehouse',
      selectContainerType: 'Select container type',
      inputQuantity: 'Quantity',
      inputLoadingPointAddress: 'Enter loading point address',
      inputDeliveryAddressText: 'Enter delivery address',
      noFclPrices: 'No FCL prices',
      noPrePortPrices: 'No Preport prices',
      noLastMilePrices: 'No Last mile prices',
      quotePending: 'Quote pending',
      quoteRejected: 'Quote rejected',
      rejectReason: 'Please input the reason for rejection...',
      rejectReminder: 'Note: After rejecting the quote, it cannot be edited again.',
      noFeeDetail: 'No fee details, please add fee items',
      noPriceDetail: 'No price details, please click the button above to add',
      selectValidPeriod: 'Select valid period',
      selectBeginDate: 'Select start date',
      selectEndDate: 'Select end date',
      selectDirectFlag: 'Select direct/transit',
      inputVoyageDays: 'Enter voyage days',
      inputDetentionFreeDays: 'Enter detention free days',
      inputDemurrageFreeDays: 'Enter demurrage free days',
      selectFeeName: 'Select fee name',
      autoFill: 'Auto fill',
      inputPrice: 'Enter price',
      inputUnitRate: 'Enter unit rate',
      selectBillingUnit: 'Select billing unit',
      selectPriceMethod: 'Select price method',
      inputSupplier: 'Enter supplier',
      inputAgentName: 'Enter agent name',
      selectBranchLine: 'Select branch line',
      selectSeaRail: 'Select sea rail'
    },

    // Units and suffixes
    units: {
      kilogram: 'KGS'
    },

    // AI recognition modal
    aiModal: {
      loadingPointTitle: 'AI Smart Address Recognition',
      deliveryTitle: 'AI Smart Address Recognition',
      loadingPointDesc: 'Please paste the address information into the text box below:',
      deliveryDesc: 'Please paste the US address information into the text box below:',
      loadingPointTip: 'Tip: The system will automatically parse province, city, district and street information, detail address will be filled into the detail address field',
      deliveryRules: 'Recognition Rules:',
      deliveryRule1: 'If contains Amazon warehouse code (ONT8/BFI4 etc.), will be set as Amazon warehouse type',
      deliveryRule2: 'If contains Ecang code (LAX203/ATL205 etc.), will be set as Ecang type',
      deliveryRule3: 'Other cases will be set as third-party address, and parse address and postal code',
      recognize: 'Recognize',
      cancel: 'Cancel'
    },

    // Dropdown options
    options: {
      alibaba: 'Alibaba Group',
      jd: 'JD Logistics Co., Ltd.',
      huawei: 'Huawei Technologies Co., Ltd.',
      xiaomi: 'Xiaomi Corporation',
      haier: 'Haier Group Corporation',
      baosteel: 'Baosteel Group Corporation Limited',
      cmg: 'China Merchants Group',
      cosco: 'COSCO SHIPPING Holdings'
    },

    // Messages
    messages: {
      saveDraftSuccess: 'Draft saved successfully',
      updateSuccess: 'Quote updated successfully',
      submitSuccess: 'Quote submitted successfully',
      saveFailed: 'Save failed',
      submitFailed: 'Submit failed',
      exportPending: 'Export rate function under development...',
      addressRecognitionSuccess: 'Address recognition successful',
      inputAddressFirst: 'Please enter address information',
      confirmSubmit: 'Confirm to submit quotation?',
      confirm: 'Confirm',
      cancel: 'Cancel',
      loading: 'Loading...',
      noData: 'No data',
      copySuccess: 'Text copied to clipboard',
      copyFailed: 'Copy failed, please copy manually',
      noAvailablePrices: 'No available quotation found. Please add and select quotations.',
      selectQuotedPriceFirst: 'Please select a quoted price first',
      rejectQuoteSuccess: 'Quote rejected successfully',
      rejectQuoteFailed: 'Quote rejection failed',
      rejectReasonEmpty: 'Reject reason cannot be empty',
      loadTemplateFailed: 'Failed to load template',
      openTemplateViewReportFailed: 'Failed to open template view report',
      quotationAllDisabled: 'At least one of trunk line, pre-port and last mile prices must be enabled',
      confirmQuoteSuccess: 'Quote confirmed successfully',
      confirmQuoteFailed: 'Quote confirmation failed',
      revokeQuoteSuccess: 'Quote revoked successfully',
      revokeQuoteFailed: 'Quote revocation failed',
      confirmRevoke: 'Confirm Revoke',
      confirmRevokeContent: 'Are you sure you want to revoke this segment quote? After revocation, the segment will return to pending status.',
      cannotRevokeAfterOrder: 'This quotation has been converted to an order and cannot be revoked',
      noPriceWarning: 'Please add rate solution first'
    },

    // Export quotation modal
    exportQuotationModal: {
      title: 'Export Quotations',
      containerTypeAndQuantity: 'Container Type & Quantity',
      copyQuotationText: 'Copy Quick Quotation Text',
      printQuotation: 'Print Quotation Document',
      printPending: 'Print function under development'
    },

    // Quick quotation text modal
    quotationTextModal: {
      title: 'Quick Quotation Text',
      description: 'The following is the quotation text generated based on your selected container types and quantities. You can copy and send it to customers:',
      placeholder: 'Please enter quotation text',
      close: 'Close',
      copyText: 'Copy Text'
    },

    // Price display labels
    priceDisplay: {
      priceNo: 'Price No',
      carrierName: 'Carrier',
      validPeriod: 'Valid Period',
      directTransit: 'Direct/Transit',
      direct: 'Direct',
      transit: 'Transit',
      voyageDays: 'Voyage Days',
      days: 'day(s)',
      detentionFreeDays: 'Free Detention Days',
      demurrageFreeDays: 'Free Demurrage Days',
      containerCharges: 'Container Charges',
      otherCharges: 'Other Charges',
      supplier: 'Supplier',
      priceMethod: 'Price Method',
      agentName: 'Agent Name'
    },

    // ZIP code related
    zipCode: {
      loading: 'Loading...',
      loadProvincesFailed: 'Failed to load provinces',
      loadCitiesFailed: 'Failed to load cities',
      loadDistrictsFailed: 'Failed to load districts',
      loadStreetsFailed: 'Failed to load streets',
      loadZipCodesFailed: 'Failed to load postal codes',
      zipCodeQueryFailed: 'Postal code query failed',
      zipCodeLoadSuccess: 'China postal codes loaded successfully',
      loadContainerTypesFailed: 'Failed to load container types',
      containerTypesLoadSuccess: 'Container types loaded successfully'
    },

    // Table columns
    columns: {
      feeName: 'Fee Name',
      currency: 'Currency',
      unitRate: 'Unit Rate',
      measure: 'Measure',
      remarks: 'Remarks',
      actions: 'Actions',
      voyageDays: 'Voyage Days',
      detentionFreeDays: 'Free Detention Days',
      demurrageFreeDays: 'Free Demurrage Days'
    }
  },

  // ======== New: AI Recognition Feature Internationalization ========
  aiGlobalRecognition: {
    // Button text
    buttonText: 'AI Recognition',
    loadingText: 'AI Recognizing...',

    // Feedback message templates
    successTemplate: 'AI recognition completed, {filled} fields filled',
    softFailure: 'Fill failed, please check input text',
    requestError: 'Request failed: {status} {error}',
    fieldRecognized: 'Recognized',

    // Modal configuration
    modal: {
      title: 'AI Smart Recognition',
      width: '600px',
      maskClosable: false
    },

    // Content text
    content: {
      description: 'Please paste the text content that needs recognition, AI will automatically extract logistics information and fill in the corresponding fields.',
      supportedItems: 'Supports recognition: port information, shipping companies, container types and quantities, weight, direct/transit, routes, cargo types, service terms, etc.',
      processing: 'AI is recognizing, please wait...'
    },

    // Placeholder
    placeholder: 'Please paste the text content to be recognized, for example:\nFrom CNSHA/Shanghai to USLAX/Los Angeles\nShipping Company: Maersk\nContainer: 2x20GP + 1x40HC\nWeight: 1200 KGS\nDirect Service, Terms: DDP\nCargo Type: General Cargo',

    // Buttons
    buttons: {
      cancel: 'Cancel',
      confirm: 'Confirm'
    },

    // Messages
    messages: {
      success: 'AI recognition completed, relevant fields have been automatically filled',
      error: 'AI recognition failed, please try again',
      emptyInput: 'Please enter the text content to be recognized'
    }
  },

  // ======== New: Inquiry AI Recognition Inline Panel Internationalization ========
  aiInquiryRecognition: {
    upload: {
      title: 'Click or drag to upload inquiry file',
      supportedFormats: 'Supports JPG/PNG/PDF/Excel/Word formats'
    },
    warning: 'If only inquiring about pre-port and last-mile fees, please prefix the information with "Pre-port" and "Last-mile" respectively.',
    textPlaceholder: 'Please paste the text content to be recognized, for example:\nFrom CNSHA/Shanghai to USLAX/Los Angeles\nShipping Company: Maersk\nContainer: 2x20GP + 1x40HC\nWeight: 1200 KGS\nDirect Service, Terms: DDP\nCargo Type: General Cargo;\nShanghai Freight Co., Ltd.\n\nStuffing Location: The system will automatically parse province/city/district/street info;\n\nDelivery Address: If a warehouse code is recognized, the system will automatically select the warehouse, otherwise it will be set as a third-party address with parsed address and postal code;',
    startButton: 'Start Recognition',
    messages: {
      emptyInput: 'Please upload a file or enter text content',
      error: 'AI recognition failed, please try again',
      filledCount: 'AI recognition completed, {filled} fields filled this time',
      noResult: 'No valid information recognized, please check the input content'
    }
  },

  // ======== New: Address Allocation Modal Internationalization ========
  addressAllocModal: {
    title: 'Address Allocation',
    description: 'The following addresses could not be automatically categorized. Please manually assign them:',
    unknownAddress: 'Address',
    selectPlaceholder: 'Please select',
    options: {
      stuffing: 'Pre-port Stuffing Location',
      delivery: 'Last-mile Delivery Address'
    },
    cancel: 'Cancel',
    confirm: 'Confirm & Fill'
  },

  // ======== New: Reset Feature Internationalization ========
  reset: {
    buttonText: 'Reset',
    confirmTitle: 'Confirm Reset',
    confirmMessage: 'Are you sure you want to reset and clear all fields?',
    confirmButton: 'Confirm',
    cancelButton: 'Cancel',
    successMessage: 'All fields have been reset'
  },



  // Combination Query
  combination: {
    title: 'Combination Solution Query',
    description: 'Query multiple rate combination solutions',
    // Page Header
    header: {
      back: 'Back',
      pageTitle: 'Combination Solution Query',
      portPrice: 'Port Price',
      trunkPrice: 'Trunk Price',
      lastMilePrice: 'Last Mile Price'
    },
    // Steps
    steps: {
      step1: {
        number: '1',
        title: 'Fill Query Conditions',
        description: 'Configure query parameters'
      },
      step2: {
        number: '2',
        title: 'Select Matching Rates',
        description: 'Choose suitable solutions'
      }
    },
    // Query Form
    form: {
      basicInfo: {
        title: 'Basic Information',
        directType: 'Direct/Transit',
        directTypeOptions: {
          notSpecified: 'Not Specified',
          direct: 'Direct',
          transit: 'Transit'
        },
        route: 'Route',
        routePlaceholder: 'Please select route',
        carrier: 'Carrier',
        carrierPlaceholder: 'Please select carrier',
        cargoType: 'Cargo Type',
        cargoTypePlaceholder: 'Please select cargo type'
      },
      addressInfo: {
        title: 'Address Information',
        pol: 'Port of Loading',
        polPlaceholder: 'Please select port of loading',
        pod: 'Port of Discharge',
        podPlaceholder: 'Please select port of discharge',
        pot: 'Port of Transit',
        potPlaceholder: 'Please select port of transit'
      },
      containerInfo: {
        title: 'Container Type & Quantity',
        containerType: 'Container Type',
        containerTypePlaceholder: 'Please select container type',
        quantity: 'Quantity',
        quantityPlaceholder: 'Please enter quantity',
        addContainer: 'Add Container',
        removeContainer: 'Remove Container'
      },
      timeInfo: {
        title: 'Time Information',
        etd: 'ETD',
        etdPlaceholder: 'Please select ETD',
        eta: 'ETA',
        etaPlaceholder: 'Please select ETA'
      },
      buttons: {
        search: 'Search',
        reset: 'Reset',
        next: 'Next',
        back: 'Previous'
      }
    },
    // Query Results
    results: {
      title: 'Query Results',
      summary: 'Query Summary',
      summaryItems: {
        route: 'Route',
        pol: 'Port of Loading',
        pod: 'Port of Discharge',
        carrier: 'Carrier',
        cargoType: 'Cargo Type',
        containers: 'Container Type & Quantity'
      },
      noResults: 'No matching combination solutions found',
      loading: 'Querying...',
      totalCount: 'Found {count} combination solutions',
      // Rate Type Labels
      rateTypes: {
        portPrice: 'Port Price',
        trunkPrice: 'Trunk Price',
        lastMilePrice: 'Last Mile Price'
      },
      // Status Labels
      status: {
        loading: 'Loading',
        loaded: 'Loaded',
        error: 'Error'
      }
    },
    // Messages
    messages: {
      searchSuccess: 'Query completed',
      searchFailed: 'Query failed',
      noDataFound: 'No matching rate data found',
      selectRateType: 'Please select at least one rate type',
      fillRequiredFields: 'Please fill in required fields',
      invalidFormData: 'Invalid form data, please check input',
      // Rate loading related
      trunkRatesLoaded: 'Trunk rate data loaded successfully',
      portRatesLoaded: 'Port rate data loaded successfully',
      lastMileRatesLoaded: 'Last mile rate data loaded successfully',
      // Error messages
      loadTrunkRatesFailed: 'Failed to load trunk rate data',
      loadPortRatesFailed: 'Failed to load port rate data',
      loadLastMileRatesFailed: 'Failed to load last mile rate data'
    }
  },

  // 尾程运价查询详情页
  lastmileDetail: {
    // 页面标题
    pageTitle: {
      create: 'Create Last Mile Rate',
      copy: 'Copy Last Mile Rate',
      edit: 'Edit Last Mile Rate',
      view: 'Last Mile Rate Details'
    },
    // 按钮
    buttons: {
      back: 'Back',
      addAddress: 'Add Address',
      removeAddress: 'Remove Address',
      expandAddress: 'Expand Address',
      collapseAddress: 'Collapse Address'
    },
    // 基本信息
    basicInfo: {
      title: 'Basic Information',
      priceNo: 'Last Mile Rate Number',
      priceNoPlaceholder: {
        copy: 'Will be regenerated after copying, format: LMR2506250001',
        create: 'Auto-generated by backend, format: LMR2506250001'
      }
    },
    // 地址信息
    address: {
      title: 'Delivery Address',
      addressItem: 'Delivery Address {index}',
      addressType: 'Address Type',
      addressTypeOptions: {
        thirdParty: 'Third Party Address',
        amazonWarehouse: 'Amazon Warehouse',
        easyWarehouse: 'Easy Warehouse',
        notSelected: 'Not Selected'
      },
      warehouseCode: 'Warehouse Code',
      warehouseCodePlaceholder: {
        amazon: 'Please select Amazon warehouse',
        easy: 'Please select Easy warehouse'
      },
      postalCode: 'Postal Code',
      detailAddress: 'Detailed Address',
      country: 'Country',
      state: 'State',
      city: 'City',
      district: 'District',
      // 地址预览
      preview: {
        addressType: 'Address Type: ',
        warehouseCode: 'Warehouse Code: ',
        postalCode: 'Postal Code: ',
        detailAddress: 'Detailed Address: '
      }
    },
    // 目的港信息
    destinationInfo: {
      title: 'Destination Port Information',
      pod: 'Port of Discharge',
      podPlaceholder: 'Please select port of discharge',
      podRequired: 'Please select port of discharge',
      agent: 'Agent Name',
      agentPlaceholder: 'Please enter agent name',
      agentRequired: 'Please enter agent name',
      etd: 'ETD',
      etdPlaceholder: 'Please select estimated departure date',
      eta: 'ETA',
      etaPlaceholder: 'Please select estimated arrival date',
      validPeriod: 'Valid Period',
      validPeriodPlaceholder: ['Start Date', 'End Date'],
      validPeriodRequired: 'Please select valid period',
      remarks: 'Remarks',
      remarksPlaceholder: 'Please enter remarks'
    },
    // 运价信息
    rateInfo: {
      title: 'Rate Information',
      // 按箱型计费
      containerBilling: {
        title: 'Container-based Billing Items',
        addItem: 'Add Billing Item',
        removeItem: 'Remove Billing Item',
        minItemsWarning: 'At least one rate item must be retained'
      },
      // 非按箱型计费
      nonContainerBilling: {
        title: 'Non-container Billing Items',
        addItem: 'Add Billing Item',
        removeItem: 'Remove Billing Item',
        minItemsWarning: 'At least one billing item must be retained'
      },
      // 表格列
      columns: {
        feeName: 'Fee Name',
        feeNamePlaceholder: 'Please select fee name',
        currency: 'Currency',
        currencyPlaceholder: 'Auto-fill',
        unit: 'Billing Unit',
        unitPlaceholder: 'Please select billing unit',
        price: 'Unit Price',
        pricePlaceholder: 'Please enter unit price',
        specialNote: 'Special Notes',
        specialNotePlaceholder: 'Special Notes'
      },
      // 箱型标签
      containerTypes: {
        '20gp': '20GP',
        '40gp': '40GP',
        '40hc': '40HC',
        '45hc': '45HC',
        '40nor': '40NOR',
        '20nor': '20NOR',
        '20hc': '20HC',
        '20tk': '20TK',
        '40tk': '40TK',
        '20ot': '20OT',
        '40ot': '40OT',
        '20fr': '20FR',
        '40fr': '40FR'
      }
    },
    // 费用名称
    feeNames: {
      lastMileFee: 'Last Mile Fee',
      deliveryFee: 'Delivery Fee',
      storageFee: 'Storage Fee',
      handlingFee: 'Handling Fee',
      fuelFee: 'Fuel Fee',
      documentFee: 'Document Fee',
      customsFee: 'Customs Fee',
      inspectionFee: 'Inspection Fee',
      operationFee: 'Operation Fee',
      insuranceFee: 'Insurance Fee'
    },
    // 消息提示
    messages: {
      missingParams: 'Missing required parameters',
      dataLoadFailed: 'Failed to load data',
      copyDataLoadFailed: 'Failed to load copy data',
      atLeastOneAddress: 'At least one address must be retained',
      priceFormat: 'LMR2506250001'
    }
  },

  // Root level common field translations
  freeContainerDays: 'Free Container Days',
  demurrageFreeDays: 'Free Demurrage Days',

  // Delivery address types
  deliveryAddressType: {
    thirdParty: 'Third Party Address',
    amazon: 'Amazon Warehouse',
    yicang: 'ECargo Warehouse'
  }
  ,

  // Cost Trial Drawer
  costTrial: {
    title: 'Cost Trial',
    boxTypeConfig: 'Container Type & Quantity',
    feeDetails: 'Fee Details',
    feeSummary: 'Fee Summary',
    totalFee: 'Total',
    toCny: 'Converted to CNY',
    exchangeRate: 'Exchange Rate',
    loadRateFailed: 'Failed to load exchange rate, please retry',
    exchangeRateValidPeriod: 'Exchange rate valid period',
    rateValidPeriod: 'Rate Valid Period',
    contactAdmin: 'If you have any questions, please contact the company administrator',
    noSuitableExchangeRate: 'No suitable exchange rate available, cannot convert to CNY',
    exchangeRateNotExist: 'Exchange Rate Not Exist',
    messages: {
      noFeeData: 'No fee data available for cost trial'
    },
    placeholders: {
      selectBoxType: 'Please select container type'
    },
    columns: {
      feeCategory: 'Fee Category',
      feeName: 'Fee Name',
      currency: 'Currency',
      bill: 'Bill',
      box: 'Box'
    },
    subtotal: 'Subtotal',
    categoryMap: {
      trunkLine: 'Trunk Line',
      prePort: 'Preport',
      lastMile: 'Last Mile'
    }
  },

  // Supplier Selection Modal
  supplierSelection: {
    title: 'Select Inquiry Target',
    tabs: {
      business: 'Business',
      supplier: 'Supplier'
    },
    searchPlaceholder: 'Search supplier name, contact person keywords',
    filter: 'Filter',
    more: 'More',
    selectAll: 'Select All',
    preferred: 'Preferred Supplier',
    region: 'Region',
    contactInfo: 'Contact Information',
    contact: 'Contact Person',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    establishedDate: 'Established Date',
    registeredCapital: 'Registered Capital',
    evaluation: 'Supplier Evaluation',
    reviews: '{count} reviews',
    productQuality: 'Product Quality',
    priceCompetitiveness: 'Price Competitiveness',
    deliveryTimeliness: 'Delivery Timeliness',
    serviceAttitude: 'Service Attitude',
    noData: 'No Data',
    selectSupplier: 'Please select a supplier to view details',
    selectedCount: '{count} supplier(s) selected',
    cancel: 'Cancel',
    confirm: 'Confirm Selection',
    confirmOnly: 'Confirm'
  },
  supplierManagement: {
    // Page Titles
    pageTitle: 'Supplier Management',
    detailPageTitle: 'Supplier Scoring Details',

    // Classification Card
    classification: {
      title: 'Supplier Classification',
      all: 'All Suppliers'
    },

    // Search & Filter
    search: {
      placeholder: 'Search supplier name, contact, or mobile number',
      location: 'Location',
      button: 'Search'
    },

    // List Card
    list: {
      title: 'Supplier List',
      defaultSort: 'Default Sort',
      sortOptions: {
        scoreDesc: 'Score Descending',
        scoreAsc: 'Score Ascending'
      },
      columns: {
        name: 'Supplier Name',
        score: 'Regional Score',
        category: 'Category',
        status: 'Cooperation Status',
        operation: 'Action'
      },
      pagination: 'Showing {pageSize} per page, total {total} records'
    },

    // Detail Page Actions
    actions: {
      editScore: 'Edit Score',
      saveScore: 'Save Score',
      cancel: 'Cancel'
    },

    // Detail Page Base Info
    baseInfo: {
      title: 'Company Information',
      contact: 'Contact:',
      phone: 'Phone:',
      email: 'Email:',
      location: 'Location:',
      totalScore: 'Total Score:'
    },

    // Scoring Card
    scoring: {
      title: 'Scoring Details',
      tabs: {
        introduce: 'Introductory Evaluation',
        usage: 'Usage Rating'
      },
      columns: {
        index: 'No.',
        scope: 'Evaluation Scope',
        maxScore: 'Points',
        item: 'Evaluation Item',
        description: 'Description',
        path: 'Query Path',
        remarks: 'Remarks',
        score: 'Score',
        totalScore: 'Total Score:'
      },
      veto: {
        placeholder: 'Please enter remarks'
      },
      displayRules: {
        perEvent: '/event',
        cap: ', cap {cap}'
      },
      rules: {
        introduce: 'Introductory Evaluation Rules:\n1. Introduced: Total score 80 (inclusive) or above\n2. Reserved: Total score between 60 (inclusive) and 79\n3. Not Introduced: Total score below 59',
        usage: 'Usage Rating Rules:\n1. Excellent: Total score 85 (inclusive) or above\n2. Good: Total score 70 (inclusive) or above\n3. Cooperating: Total score 60 (inclusive) or above\n4. Rectifying: Total score below 59'
      }
    },

    // Status Enums
    enums: {
      introduce: {
        introduced: 'Introduced',
        reserved: 'Reserved',
        rejected: 'Rejected'
      },
      usage: {
        excellent: 'Excellent',
        good: 'Good',
        cooperating: 'Cooperating',
        rectifying: 'Rectifying'
      },
      cooperation: {
        cooperating: 'Cooperating',
        pending: 'Pending',
        evaluating: 'Evaluating',
        terminated: 'Terminated'
      }
    },

    // Role Translations
    roles: {
      ROLE_SHIPPER: 'Shipper',
      ROLE_CONSIGNEE: 'Consignee',
      ROLE_NOTIFY: 'Notify Party',
      ROLE_CARRIER: 'Carrier',
      ROLE_AIRLINE: 'Airline',
      ROLE_TRUCK: 'Trucker',
      ROLE_WAREHOUSE: 'Warehouse',
      ROLE_CUSTOMS: 'Customs Broker',
      ROLE_FORWARDER: 'Forwarder',
      ROLE_AGENT: 'Agent',
      ROLE_SUPPLIER: 'Supplier',
      ROLE_FACTORY: 'Factory/Manufacturer',
      ROLE_TRADER: 'Trader'
    },

    // Messages
    messages: {
      loadRolesFailed: 'Failed to load supplier role list',
      loadDataFailed: 'Failed to load data',
      saveSuccess: 'Saved successfully',
      saveFailed: 'Failed to save scoring',
      noChanges: 'No changes detected, nothing to save',
      enterpriseIdRequired: 'Enterprise ID is required',
      scoreRangeError: 'Score is out of the allowed range',
      loadDetailFailed: 'Failed to load evaluation details',
      getEnterpriseIdFailed: 'Unable to get enterprise ID',
      enterpriseIdPlaceholder: 'Enterprise {id}'
    }
  }
}